import * as yup from 'yup';

import {
    Alert,
    Badge,
    ButtonGroup,
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    Col,
    Form,
    FormGroup,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
    Spinner,
} from 'reactstrap';
import { AmsPermissionClaimType, authService } from '../../services/Auth';
import {
    AuspiceDto,
    CitationDto,
    Institution,
    InstitutionTypeDto,
    InstitutionalAccreditor,
    MeetingDateDto,
    ProfessionStandardsDto,
    ProgramStatusHistory,
    RecommendationDto,
    RecommendationExtension,
    RecommendationSatellite,
    UserInfo,
    ProfessionDetailsDto,
    ProfessionsConcentrationDto,
} from '../../common/Types';
import { Button, Input, PageTitle, Ribbon } from '../../components';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Countries, DegreeType, DegreeTypes, ExtensionStatus, ProgramStatus, States } from '../../common/Data';
import { parseUserInfo } from '../../common/Utils';
import React, { useEffect, useState } from 'react';
import { Redirect, useLocation, useParams } from 'react-router-dom';

import CitationForm from '../../components/CitationForm';
import { RecommendationSatellite as RecommendationSatelliteForm } from '../../components';
import Skeleton from 'react-loading-skeleton';
import { institutionService } from '../../services/InstitutionService';
import moment from 'moment';
import { personnelService } from '../../services/Personnel';
import { professionService } from '../../services/ProfessionAdministration';
import { programService } from '../../services/ProgramService';
import { recommendationService } from '../../services/RecommendationService';
import { systemAdministration } from '../../services/SystemAdministration';
import { toast } from 'react-toastify';

// Treat 1900-01-01 (smalldatetime minimum) as no date
const formatAccreditedDate = (date: Date | string | undefined | null): string | undefined => {
    if (!date) return undefined;
    const m = moment(date);
    if (!m.isValid() || m.isSameOrBefore('1900-01-01', 'day')) return undefined;
    return m.format('YYYY-MM-DD');
};

interface RecommendationParams {
    recommendationId?: string;
}

const Recommendation = () => {
    const [citations, setCitations] = useState<CitationDto[]>();

    // Email lookup state
    const [isLookingUpPD, setIsLookingUpPD] = useState(false);
    const [pdLookupMessage, setPdLookupMessage] = useState<string | null>(null);
    const [isLookingUpDean, setIsLookingUpDean] = useState(false);
    const [deanLookupMessage, setDeanLookupMessage] = useState<string | null>(null);
    const [isLookingUpCeo, setIsLookingUpCeo] = useState(false);
    const [ceoLookupMessage, setCeoLookupMessage] = useState<string | null>(null);

    const recommendationSchema = yup
        .object({
            recommendationId: yup.number().required(),
            caahepinstitutionId: yup
                .number()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return ov === '' || isNaN(ov) || !ov ? undefined : +cv;
                }),
            caahepprogramId: yup
                .number()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return ov === '' || isNaN(ov) || !ov ? undefined : +cv;
                }),
            institutionalAccreditorId: yup
                .number()
                .required('Institutional Accreditor is required')
                .transform((cv, ov) => {
                    return (ov || -1) === -1 ? undefined : +cv;
                }),
            professionStandardId: yup.number().required(),
            citations: yup.number().required().default(0),
            statusId: yup.number().required(),
            effectiveDate: yup.date().notRequired().nullable(),
            expirationDate: yup.date().notRequired().nullable(),
            prdue: yup
                .date()
                .test('prdue-required', 'PR Due is required', function (value) {
                    if ((citations?.length || 0) > 0) {
                        return value != null;
                    }
                    return true;
                })
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            nextFullEval: yup
                .date()
                .required('Next Full Evaluation is required')
                .typeError('Next Full Evaluation is required'),
            meetingDateId: yup.number().required(),
            meetingDate: yup.date().required(),
            institutionTitle: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            institutionTypeId: yup
                .number()
                .required()
                .transform((cv, ov) => {
                    return ov === '' || isNaN(ov) || !ov ? undefined : cv;
                }),
            institutionAuspiceId: yup
                .number()
                .required()
                .transform((cv, ov) => {
                    return ov === '' || isNaN(ov) || !ov ? undefined : cv;
                }),
            institutionAddress: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            institutionAddress2: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            institutionAddress3: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            institutionAddress4: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            institutionCity: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            institutionState: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            institutionZip: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            institutionCountry: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            institutionPhone: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            institutionExtension: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            institutionWebSite: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            programProfessionId: yup.number().required(),
            programConcentrationId: yup
                .number()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return ov === '' || isNaN(ov) || !ov ? undefined : +cv;
                }),
            professionLevelId: yup
                .number()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return ov === '' || isNaN(ov) || !ov ? undefined : +cv;
                }),
            programAddOnTrackId: yup
                .number()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return ov === '' || isNaN(ov) || !ov ? undefined : +cv;
                }),
            programTitle: yup.string().required('Program Title is required'),
            programName: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            programAddress: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            programAddress2: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            programAddress3: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            programAddress4: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            programCity: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            programState: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            programZip: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            programCountry: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            programDateFirstAccredited: yup.date().notRequired().nullable(),
            programDistance: yup.boolean().required().default(false),
            programMilitaryPersonnelOnly: yup.boolean().required().default(false),
            programWebSite: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            programOutcomesUrl: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            programDirectorFirstName: yup
                .string()
                .required('PD First Name is required')
                .typeError('PD First Name is required'),
            programDirectorLastName: yup
                .string()
                .required('PD Last Name is required')
                .typeError('PD Last Name is required'),
            programDirectorPhone: yup.string().required('PD Phone is required').typeError('PD Phone is required'),
            programDirectorExtension: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            programDirectorTitle: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            programDirectorCredentials: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            programDirectorSalutations: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            programDirectorEmail: yup.string().required('PD Email is required').typeError('PD Email is required'),
            deanFirstName: yup.string().when('deanNotAvailable', (deanNotAvailable, schema) => {
                return !deanNotAvailable
                    ? schema.required('Dean First Name is required').typeError('Dean First Name is required')
                    : schema
                          .notRequired()
                          .nullable()
                          .transform((cv, ov) => {
                              return !ov ? undefined : cv;
                          });
            }),
            deanLastName: yup.string().when('deanNotAvailable', (deanNotAvailable, schema) => {
                return !deanNotAvailable
                    ? schema.required('Dean Last Name is required').typeError('Dean Last Name is required')
                    : schema
                          .notRequired()
                          .nullable()
                          .transform((cv, ov) => {
                              return !ov ? undefined : cv;
                          });
            }),
            deanTitle: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            deanCredentials: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            deanSalutations: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            deanEmail: yup.string().when('deanNotAvailable', (deanNotAvailable, schema) => {
                return !deanNotAvailable
                    ? schema.required('Dean Email is required').typeError('Dean Email is required')
                    : schema
                          .notRequired()
                          .nullable()
                          .transform((cv, ov) => {
                              return !ov ? undefined : cv;
                          });
            }),
            ceofirstName: yup.string().required('CEO First Name is required').typeError('CEO First Name is required'),
            ceolastName: yup.string().required('CEO Last Name is required').typeError('CEO Last Name is required'),
            ceotitle: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            ceocredentials: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            ceosalutations: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            createdOn: yup.date().notRequired().nullable(),
            // createdBy?: string;
            modifiedOn: yup.date().notRequired().nullable(),
            // modifiedBy?: string;
            isMerged: yup.boolean().required().default(false),
            // mergedBy?: string;
            mergedByString: yup
                .string()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return !ov ? undefined : cv;
                }),
            mergedOn: yup
                .date()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return ov === '' || !ov ? undefined : cv;
                }),
            active: yup.boolean().required().default(true),
            programDirectorNotAvailable: yup.boolean().required().default(false),
            deanNotAvailable: yup.boolean().required().default(false),
            ceoNotAvailable: yup.boolean().required().default(false),
            // coaletterCc1?: string;
            // coaletterCc2?: string;
            // coaletterCc3?: string;
            transferFromProgramId: yup
                .number()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return ov === '' || isNaN(ov) || !ov ? undefined : +cv;
                }),
            transferFromInstitutionId: yup
                .number()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return ov === '' || isNaN(ov) || !ov ? undefined : +cv;
                }),
            transferToInstitutionId: yup
                .number()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return ov === '' || isNaN(ov) || !ov ? undefined : +cv;
                }),
            createdByString: yup.string().nullable().notRequired(),
            editedByString: yup.string().notRequired().nullable(),
            isCitationSectionComplete: yup.boolean().required().default(false),
            isDeanSectionComplete: yup.boolean().required().default(false),
            isCeoSectionComplete: yup.boolean().required().default(false),
            isInstitutionSectionComplete: yup.boolean().required().default(false),
            isProgramDirectorSectionComplete: yup.boolean().required().default(false),
            isProgramSectionComplete: yup.boolean().required().default(false),
            isRecommendationSectionComplete: yup.boolean().required().default(false),
            degreeTypeId: yup
                .number()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return ov === '' || isNaN(ov) || !ov ? undefined : +cv;
                }),
        })
        .defined();

    const {
        handleSubmit,
        reset,
        watch,
        control,
        setValue,
        formState: { errors, ...formState },
    } = useForm({
        resolver: yupResolver(recommendationSchema as any),
        defaultValues: {} as RecommendationDto,
    });

    const { recommendationId } = useParams<RecommendationParams>();
    const [transferFromInstitution, setTransferFromInstitution] = useState<Institution>();
    const [professionId, setProfessionId] = useState<number>();
    const [profession, setProfession] = useState<ProfessionDetailsDto>();
    const [programId, setProgramId] = useState<number>();
    const [statusHistory, setStatusHistory] = useState<ProgramStatusHistory[]>();
    const [standards, setStandards] = useState<ProfessionStandardsDto[]>();
    const [dates, setDates] = useState<MeetingDateDto[]>();
    const [institutionTypes, setInstitutionTypes] = useState<InstitutionTypeDto[]>();
    const [auspices, setAuspices] = useState<AuspiceDto[]>();
    const [accreditors, setAccreditors] = useState<InstitutionalAccreditor[]>();
    const [selectedCitation, setSelectedCitation] = useState<CitationDto>();
    const [showCitationModal, setShowCitationModal] = useState<boolean>(false);
    const [showCitationRemoveModal, setShowCitationRemoveModal] = useState<boolean>(false);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [reloadCitations, setReloadCitations] = useState<boolean>(true);
    const [removeCitation, setRemoveCitation] = useState<CitationDto>();
    const [backToList, setBackToList] = useState<boolean>(false);
    const [isMergeCommand, setIsMergeCommand] = useState<boolean>(false);
    const [useInstitutionAddress, setUseInstitutionAddress] = useState<boolean>(false);

    const toggleCitationModal = () => setShowCitationModal(!showCitationModal);
    const toggleCitationRemoveModal = () => setShowCitationRemoveModal(!showCitationRemoveModal);
    const toggleRemoveModal = () => setShowRemoveModal(!showRemoveModal);

    const [satellites, setSatellites] = useState<RecommendationSatellite[]>();
    const [editSatellite, setEditSatellite] = useState<RecommendationSatellite>();
    const [reloadSatellites, setReloadSatellites] = useState<boolean>(true);
    const [showSatellite, setShowSatellite] = useState<boolean>(false);
    const toggleShowSatellite = () => setShowSatellite(!showSatellite);

    const [extensions, setExtensions] = useState<RecommendationExtension[]>([]);
    const [reloadExtensions, setReloadExtensions] = useState<boolean>(true);
    const [showExtensionModal, setShowExtensionModal] = useState<boolean>(false);
    const [editExtension, setEditExtension] = useState<RecommendationExtension | undefined>();
    const toggleShowExtensionModal = () => setShowExtensionModal(!showExtensionModal);

    const [showChangeInst, setShowChangeInst] = useState<boolean>(false);
    const toggleShowChangeInst = () => setShowChangeInst(!showChangeInst);
    const [changeInstId, setChangeInstId] = useState<number>();
    const [changeNewInst, setChangeNewInst] = useState<boolean>(false);

    const location = useLocation();
    const isNew = location.pathname.indexOf('new') >= 0;

    const recId = +(recommendationId || 0);

    const programStatuses = [
        ProgramStatus.Continuing,
        ProgramStatus.Initial,
        ProgramStatus.Probation,
        ProgramStatus.Withdraw,
        ProgramStatus.Withhold,
    ];

    useEffect(() => {
        const loadRecommendation = () => {
            if (recId > 0) {
                recommendationService
                    .getById(recId)
                    .then((rec) => {
                        rec.effectiveDate = rec.effectiveDate
                            ? moment(rec.effectiveDate).format('YYYY-MM-DD')
                            : undefined;
                        rec.prdue = rec.prdue ? moment(rec.prdue).format('YYYY-MM-DD') : undefined;
                        rec.nextFullEval = rec.nextFullEval ? moment(rec.nextFullEval).format('YYYY-MM-DD') : undefined;
                        rec.programDateFirstAccredited = formatAccreditedDate(rec.programDateFirstAccredited);
                        if (rec.createdOn) {
                            rec.createdOn = moment.utc(rec.createdOn).local().toDate();
                        }

                        // Don't autofill statusId - CoA must enter it manually
                        // Don't autofill nextFullEval - CoA must enter it manually
                        if (!rec.statusId || rec.statusId === 0) {
                            rec.statusId = undefined;
                            rec.nextFullEval = undefined;
                        }

                        reset(rec);
                        setProfessionId(rec.programProfessionId);
                        setProgramId(rec.caahepprogramId);
                        //setSatellites((rec.satellites || []).sort((a, b) => (a.title || '').localeCompare(b.title || '')));

                        if (rec.transferFromInstitutionId) {
                            institutionService
                                .getInstitution(rec.transferFromInstitutionId)
                                .then((inst) => {
                                    setTransferFromInstitution(inst);
                                })
                                .catch(() => {
                                    toast.error('Error loading transfer from institution detail');
                                });
                        }
                    })
                    .catch(() => {
                        toast.error('Error loading recommendation');
                    });
            }
        };

        const loadMeetingDates = () => {
            systemAdministration
                .getMeetingDates()
                .then((meetings) => {
                    setDates(
                        meetings
                            .filter((m) => m.active === true)
                            .sort((a, b) => moment.utc(b.meetingDate).unix() - moment.utc(a.meetingDate).unix()),
                    );
                })
                .catch(() => {
                    toast.error('Error loading meeting dates');
                });
        };

        const loadInstitutionTypes = () => {
            institutionService
                .getTypes()
                .then((insTypes) => {
                    setInstitutionTypes(insTypes.filter((it) => it.isActive === true));
                })
                .catch(() => {
                    toast.error('Error loading institution types');
                });
        };

        const loadAuspices = () => {
            institutionService
                .getAuspice()
                .then((aus) => {
                    setAuspices(aus.filter((a) => a.isActive === true));
                })
                .catch(() => {
                    toast.error('Error loading auspices');
                });
        };

        const loadAccreditors = () => {
            systemAdministration
                .getInstitutionalAccreditors()
                .then((results) => {
                    setAccreditors([
                        ...results.filter((a) => a.isActive === true).sort((a, b) => a.title.localeCompare(b.title)),
                    ]);
                })
                .catch(() => {
                    toast.error('Error loading institutional accreditors');
                });
        };

        loadRecommendation();
        loadMeetingDates();
        loadInstitutionTypes();
        loadAuspices();
        loadAccreditors();
    }, [recId, reset]);

    useEffect(() => {
        const loadCitations = () => {
            if (recId && reloadCitations) {
                setReloadCitations(false);
                recommendationService
                    .getCitationsByRecommendationId(recId)
                    .then((cits) => {
                        setCitations(cits);
                    })
                    .catch(() => {
                        toast.error('Error loading citations');
                    });
            }
        };

        loadCitations();
    }, [recId, reloadCitations]);

    useEffect(() => {
        const loadProfessionStandards = () => {
            if (professionId) {
                professionService
                    .getProfessionStandards(professionId)
                    .then((stds) => {
                        setStandards(stds.sort((a, b) => (a.standardYear || 0) - (b.standardYear || 0)));
                    })
                    .catch(() => {
                        toast.error('Error loading standards versions');
                    });
            }
        };

        const loadProfession = () => {
            if (professionId) {
                professionService
                    .getProfessionDetails(professionId)
                    .then((profession) => {
                        setProfession(profession);
                    })
                    .catch(() => {
                        toast.error('Error loading profession');
                    });
            }
        };

        loadProfessionStandards();
        loadProfession();
    }, [professionId]);

    useEffect(() => {
        const loadStatusHistory = () => {
            if (programId) {
                programService
                    .getProgramStatusHistoryByProgramId(programId)
                    .then((hist) => {
                        setStatusHistory(hist);
                    })
                    .catch(() => {
                        toast.error('Error loading standards versions');
                    });
            }
        };

        loadStatusHistory();
    }, [programId]);

    useEffect(() => {
        if (reloadSatellites) {
            setReloadSatellites(false);

            recommendationService
                .getSatellitesByRecommendationId(recId)
                .then((s) => {
                    setSatellites(s.sort((a, b) => (a.title || '').localeCompare(b.title || '')));
                })
                .catch(() => {
                    toast.error('Error loading satellites');
                });
        }
    }, [recId, reloadSatellites]);

    useEffect(() => {
        if (reloadExtensions && recId > 0) {
            setReloadExtensions(false);

            recommendationService
                .getExtensionsByRecommendationId(recId)
                .then((exts) => {
                    setExtensions(exts);
                })
                .catch(() => {
                    toast.error('Error loading extensions');
                });
        }
    }, [recId, reloadExtensions]);

    const recommendation = watch();

    // Watch email fields to control other field states
    const isPDEmailEntered = !!recommendation.programDirectorEmail && recommendation.programDirectorEmail.trim().length > 0;
    const isDeanEmailEntered = !!recommendation.deanEmail && recommendation.deanEmail.trim().length > 0;
    const isCeoEmailEntered = !!recommendation.ceoemail && recommendation.ceoemail.trim().length > 0;

    const createdByInfo = parseUserInfo<UserInfo>(recommendation.createdByString);
    const modifiedByInfo = parseUserInfo<UserInfo>(recommendation.modifiedByString);
    const mergedByInfo = parseUserInfo<UserInfo>(recommendation.mergedByString);

    // Email lookup handlers for auto-filling person details
    const lookupProgramDirectorByEmail = async (email: string) => {
        setPdLookupMessage(null);
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email || !emailPattern.test(email)) return;

        setIsLookingUpPD(true);
        try {
            const person = await personnelService.getPersonByEmail(email);
            if (person && person.peopleId) {
                setValue('programDirectorFirstName', person.firstName || '');
                setValue('programDirectorLastName', person.lastName || '');
                setValue('programDirectorTitle', person.title || '');
                setValue('programDirectorCredentials', person.credentials || '');
                setValue('programDirectorSalutations', person.salutations || '');
                setValue('programDirectorPhone', person.phone || '');
                setValue('programDirectorExtension', person.extension || '');
                setPdLookupMessage(`Found: ${person.firstName} ${person.lastName}`);
            }
        } catch (error) {
            console.log('Email lookup failed:', error);
        } finally {
            setIsLookingUpPD(false);
        }
    };

    const lookupDeanByEmail = async (email: string) => {
        setDeanLookupMessage(null);
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email || !emailPattern.test(email)) return;

        setIsLookingUpDean(true);
        try {
            const person = await personnelService.getPersonByEmail(email);
            if (person && person.peopleId) {
                setValue('deanFirstName', person.firstName || '');
                setValue('deanLastName', person.lastName || '');
                setValue('deanTitle', person.title || '');
                setValue('deanCredentials', person.credentials || '');
                setValue('deanSalutations', person.salutations || '');
                setDeanLookupMessage(`Found: ${person.firstName} ${person.lastName}`);
            }
        } catch (error) {
            console.log('Email lookup failed:', error);
        } finally {
            setIsLookingUpDean(false);
        }
    };

    const lookupCeoByEmail = async (email: string) => {
        setCeoLookupMessage(null);
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email || !emailPattern.test(email)) return;

        setIsLookingUpCeo(true);
        try {
            const person = await personnelService.getPersonByEmail(email);
            if (person && person.peopleId) {
                setValue('ceofirstName', person.firstName || '');
                setValue('ceolastName', person.lastName || '');
                setValue('ceotitle', person.title || '');
                setValue('ceocredentials', person.credentials || '');
                setValue('ceosalutations', person.salutations || '');
                setCeoLookupMessage(`Found: ${person.firstName} ${person.lastName}`);
            }
        } catch (error) {
            console.log('Email lookup failed:', error);
        } finally {
            setIsLookingUpCeo(false);
        }
    };

    const viewCitation = (citation: CitationDto) => {
        setSelectedCitation(citation);
        toggleCitationModal();
    };

    const newCitation = () => {
        setSelectedCitation({
            citationId: 0,
            isDeleted: false,
            programId: recommendation.caahepprogramId,
            recommendationId: recommendation.recommendationId,
            standardsDesignationId: -1,
            detail: [],
        });
        toggleCitationModal();
    };

    const deleteCitation = (citationId: number) => {
        const toastId = toast.info('Removing citation...');

        recommendationService
            .removeCitation(citationId)
            .then((cit) => {
                toast.update(toastId, {
                    render: 'Citation removed successfully',
                    type: 'success',
                });
                setReloadCitations(true);
                toggleCitationRemoveModal();
            })
            .catch(() => {
                toast.update(toastId, {
                    render: 'Error removing citation',
                    type: 'error',
                });
            });
    };

    const deleteRecommendation = (recommendationId: number) => {
        const toastId = toast.info('Deleting recommendation...');

        recommendationService
            .removeRecommendation(recommendationId)
            .then((result) => {
                toast.update(toastId, {
                    render: 'Recommendation deleted successfully',
                    type: 'success',
                });

                setBackToList(true);
            })
            .catch(() => {
                toast.update(toastId, {
                    render: 'Error deleting recommendation',
                    type: 'error',
                });
            });
    };

    const toggleSatellite = (s: RecommendationSatellite) => {
        const removeRestore = s.isActive === true ? 'Removing' : 'Restoring';
        const removeRestoreLc = s.isActive === true ? 'removing' : 'restoring';
        const removeRestorePt = s.isActive === true ? 'removed' : 'restored';

        const toastId = toast.info(`${removeRestore} satellite...`);

        recommendationService
            .saveSatellite({
                ...s,
                isActive: !s.isActive,
            })
            .then((s) => {
                toast.update(toastId, {
                    render: `Satellite ${removeRestorePt} successfully`,
                    type: 'success',
                });
                setReloadSatellites(true);
            })
            .catch(() => {
                toast.update(toastId, {
                    render: `Error ${removeRestoreLc} satellite`,
                    type: 'error',
                });
            });
    };

    const getExtensionStatusText = (ext: RecommendationExtension): string => {
        const statusId = ext.currentStatusId || ext.activeStatus;
        let baseStatus = 'Unknown';
        
        // Map status IDs to text
        switch (statusId) {
            case 1:
                baseStatus = 'Approved';
                break;
            case 2:
                baseStatus = 'On Notice';
                break;
            case 3:
                baseStatus = 'Removed';
                break;
            case 4:
                baseStatus = 'Not Applicable';
                break;
        }
        
        // If recommendation hasn't been merged
        if (!hasBeenMerged()) {
            // Check if this is a new extension added in this recommendation
            // by comparing the dateOfInitialApproval with the recommendation's effectiveDate
            const extApprovalDate = ext.dateOfInitialApproval ? new Date(ext.dateOfInitialApproval).toISOString().split('T')[0] : null;
            const recEffectiveDate = recommendation.effectiveDate ? new Date(recommendation.effectiveDate).toISOString().split('T')[0] : null;
            
            // New extension (dates match, indicating it was added with this recommendation)
            if (extApprovalDate && recEffectiveDate && extApprovalDate === recEffectiveDate) {
                return 'Approved - Pending';
            }
            // Existing extension marked for deletion
            if (ext.isDeleted) {
                return 'Removed - Pending';
            }
        }
        
        return baseStatus;
    };

    const getExtensionStatusColor = (ext: RecommendationExtension): string => {
        // If recommendation hasn't been merged
        if (!hasBeenMerged()) {
            const extApprovalDate = ext.dateOfInitialApproval ? new Date(ext.dateOfInitialApproval).toISOString().split('T')[0] : null;
            const recEffectiveDate = recommendation.effectiveDate ? new Date(recommendation.effectiveDate).toISOString().split('T')[0] : null;
            
            // Check for pending changes (new or deleted)
            if ((extApprovalDate && recEffectiveDate && extApprovalDate === recEffectiveDate) || ext.isDeleted) {
                return 'warning'; // Yellow for pending changes
            }
        }
        
        // Use color based on actual status
        switch (ext.currentStatusId || ext.activeStatus) {
            case 1:
                return 'success'; // Green for approved
            case 2:
                return 'warning'; // Yellow for on notice
            case 3:
                return 'danger'; // Red for removed
            case 4:
                return 'secondary'; // Gray for not applicable
            default:
                return 'secondary';
        }
    };
    
    const isNewlyAddedExtension = (ext: RecommendationExtension): boolean => {
        // Check if this extension was added as part of this recommendation (not existing on program)
        // by comparing dateOfInitialApproval with the recommendation's meeting date
        if (ext.dateOfInitialApproval && recommendation.meetingDate) {
            const approvalDate = new Date(ext.dateOfInitialApproval).toDateString();
            const meetingDate = new Date(recommendation.meetingDate).toDateString();
            return approvalDate === meetingDate;
        }
        return false;
    };

    const shouldShowExtension = (ext: RecommendationExtension): boolean => {
        // Don't show extensions that were newly added to this recommendation and then deleted
        // These can be fully removed (no restore option needed)
        if (ext.isDeleted && isNewlyAddedExtension(ext)) {
            return false;
        }

        // For unmerged recommendations, show existing extensions (including ones marked for removal)
        // so they appear as "Removed - Pending"
        if (!hasBeenMerged()) {
            return true;
        }

        // For merged recommendations, show all extensions
        return true;
    };

    const toggleExtension = (ext: RecommendationExtension) => {
        const removeRestore = !ext.isDeleted ? 'Removing' : 'Restoring';
        const removeRestoreLc = !ext.isDeleted ? 'removing' : 'restoring';
        const removeRestorePt = !ext.isDeleted ? 'removed' : 'restored';
        const itemType = ext.concentrationId ? 'concentration' : 'add-on track';
        const itemName = ext.concentrationTitle || ext.addOnTrackTitle || '';

        const toastId = toast.info(`${removeRestore} ${itemType}...`);

        recommendationService
            .saveExtension({
                ...ext,
                isDeleted: !ext.isDeleted,
            })
            .then(() => {
                toast.update(toastId, {
                    render: `${itemName} ${removeRestorePt} successfully`,
                    type: 'success',
                });
                setReloadExtensions(true);
            })
            .catch(() => {
                toast.update(toastId, {
                    render: `Error ${removeRestoreLc} ${itemType}`,
                    type: 'error',
                });
            });
    };

    const submitForm = (values: RecommendationDto) => {
        if (isMergeCommand) {
            const toastId = toast.info('Merging recommendation...');

            recommendationService
                .mergeRecommendation(values)
                .then((result) => {
                    toast.update(toastId, {
                        render: 'Recommendation merged successfully',
                        type: 'success',
                    });

                    setBackToList(true);
                })
                .catch(() => {
                    toast.update(toastId, {
                        render: 'Error merging recommendation',
                        type: 'error',
                    });
                });
        } else {
            const toastId = toast.info('Saving recommendation...');

            recommendationService
                .updateRecommendation(values)
                .then((result) => {
                    toast.update(toastId, {
                        render: 'Recommendation saved successfully',
                        type: 'success',
                    });

                    result.effectiveDate = result.effectiveDate
                        ? moment(result.effectiveDate).format('YYYY-MM-DD')
                        : undefined;
                    result.prdue = result.prdue ? moment(result.prdue).format('YYYY-MM-DD') : undefined;
                    result.nextFullEval = result.nextFullEval
                        ? moment(result.nextFullEval).format('YYYY-MM-DD')
                        : undefined;
                    result.programDateFirstAccredited = formatAccreditedDate(result.programDateFirstAccredited);

                    reset(result);
                    setBackToList(true);
                })
                .catch(() => {
                    toast.update(toastId, {
                        render: 'Error saving recommendation',
                        type: 'error',
                    });
                });
        }
    };

    const canMerge = authService.hasPermission(AmsPermissionClaimType, 'recommendation');
    const cannotEditInstitution = !canMerge && +(recommendation.caahepinstitutionId || 0) > 0;
    const isAdmin = authService.hasPermission(AmsPermissionClaimType, 'systemadmin');
    const isExistingProgramForNonAdmin = !isAdmin && +(recommendation.caahepprogramId || 0) > 0;

    const ableToMerge = (rec: RecommendationDto): boolean => {
        return (
            rec.isCeoSectionComplete &&
            rec.isCitationSectionComplete &&
            rec.isDeanSectionComplete &&
            rec.isInstitutionSectionComplete &&
            rec.isProgramDirectorSectionComplete &&
            rec.isProgramSectionComplete &&
            rec.isRecommendationSectionComplete
        );
    };

    const acceptAll = () => {
        setValue('isInstitutionSectionComplete', true);
        setValue('isCeoSectionComplete', true);
        setValue('isCitationSectionComplete', true);
        setValue('isDeanSectionComplete', true);
        setValue('isProgramDirectorSectionComplete', true);
        setValue('isProgramSectionComplete', true);
        setValue('isRecommendationSectionComplete', true);
    };

    const setEffectiveDate = (date) => {
        setValue('effectiveDate', moment(date).format('YYYY-MM-DD'));
    };

    useEffect(() => {
        if (useInstitutionAddress) {
            setValue('programAddress', recommendation.institutionAddress);
            setValue('programAddress2', recommendation.institutionAddress2);
            setValue('programAddress3', recommendation.institutionAddress3);
            setValue('programAddress4', recommendation.institutionAddress4);
            setValue('programCity', recommendation.institutionCity);
            setValue('programState', recommendation.institutionState);
            setValue('programZip', recommendation.institutionZip);
            setValue('programCountry', recommendation.institutionCountry);
        }
    }, [
        useInstitutionAddress,
        recommendation.institutionAddress,
        recommendation.institutionAddress2,
        recommendation.institutionAddress3,
        recommendation.institutionAddress4,
        recommendation.institutionCity,
        recommendation.institutionState,
        recommendation.institutionZip,
        recommendation.institutionCountry,
        setValue,
    ]);

    const hasBeenMerged = (): boolean => {
        return recommendation.isMerged === true || recommendation.isMerged?.toString() === 'true';
    };

    const updateInstitution = (): void => {
        if (changeNewInst) {
            const toastId = toast.info('Updating recommendation...');
            recommendationService
                .updateInstitution(+recommendation.recommendationId)
                .then((rec) => {
                    toast.update(toastId, {
                        type: 'success',
                        render: 'Recommendation updated successfully',
                    });
                    rec.effectiveDate = rec.effectiveDate ? moment(rec.effectiveDate).format('YYYY-MM-DD') : undefined;
                    rec.prdue = rec.prdue ? moment(rec.prdue).format('YYYY-MM-DD') : undefined;
                    rec.nextFullEval = rec.nextFullEval ? moment(rec.nextFullEval).format('YYYY-MM-DD') : undefined;
                    rec.programDateFirstAccredited = formatAccreditedDate(rec.programDateFirstAccredited);

                    reset(rec);
                    toggleShowChangeInst();
                })
                .catch(() => {
                    toast.update(toastId, {
                        type: 'error',
                        render: 'Error updating recommendation',
                    });
                });
        } else {
            if (changeInstId && !isNaN(changeInstId)) {
                const toastId = toast.info('Updating recommendation...');
                recommendationService
                    .updateInstitution(+recommendation.recommendationId, changeInstId)
                    .then((rec) => {
                        toast.update(toastId, {
                            type: 'success',
                            render: 'Recommendation updated successfully',
                        });
                        rec.effectiveDate = rec.effectiveDate
                            ? moment(rec.effectiveDate).format('YYYY-MM-DD')
                            : undefined;
                        rec.prdue = rec.prdue ? moment(rec.prdue).format('YYYY-MM-DD') : undefined;
                        rec.nextFullEval = rec.nextFullEval ? moment(rec.nextFullEval).format('YYYY-MM-DD') : undefined;
                        rec.programDateFirstAccredited = formatAccreditedDate(rec.programDateFirstAccredited);

                        reset(rec);
                        toggleShowChangeInst();
                    })
                    .catch(() => {
                        toast.update(toastId, {
                            type: 'error',
                            render: 'Error updating recommendation',
                        });
                    });
            } else {
                toast.warn('Institution ID must be a number');
            }
        }
    };

    if (recId === 0 || backToList) {
        return <Redirect to={`/recommendations/list`} />;
    } else {
        return (
            <>
                <PageTitle title={`Recommendation Detail`} />

                {(!recommendation || recommendation?.recommendationId === 0) && <Skeleton count={5} />}
                {(recommendation?.recommendationId || 0) > 0 && (
                    <Form noValidate onSubmit={handleSubmit(submitForm)}>
                        <Controller
                            control={control}
                            name={`recommendationId`}
                            render={({ field }) => <input type={'hidden'} {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            control={control}
                            name={`citations`}
                            render={({ field }) => <input type={'hidden'} {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            control={control}
                            name={`meetingDate`}
                            render={({ field }) => (
                                <input type={'hidden'} {...field} value={field.value instanceof Date ? field.value.toISOString() : field.value || ''} />
                            )}
                        />
                        <Controller
                            control={control}
                            name={`isMerged`}
                            render={({ field }) => (
                                <input type={'hidden'} {...field} value={String(field.value || '')} />
                            )}
                        />
                        <Controller
                            control={control}
                            name={`active`}
                            render={({ field }) => (
                                <input type={'hidden'} {...field} value={String(field.value || '')} />
                            )}
                        />
                        <Controller
                            control={control}
                            name={`createdByString`}
                            render={({ field }) => <input type={'hidden'} {...field} value={field.value || ''} />}
                        />
                        <Controller
                            control={control}
                            name={`createdOn`}
                            render={({ field }) => (
                                <input type={'hidden'} {...field} value={field.value instanceof Date ? field.value.toISOString() : field.value || ''} />
                            )}
                        />
                        <Controller
                            control={control}
                            name={`modifiedByString`}
                            render={({ field }) => <input type={'hidden'} {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            control={control}
                            name={`modifiedOn`}
                            render={({ field }) => (
                                <input type={'hidden'} {...field} value={field.value instanceof Date ? field.value.toISOString() : field.value || ''} />
                            )}
                        />
                        <Controller
                            control={control}
                            name={`mergedByString`}
                            render={({ field }) => <input type={'hidden'} {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            control={control}
                            name={`mergedOn`}
                            render={({ field }) => (
                                <input type={'hidden'} {...field} value={field.value instanceof Date ? field.value.toISOString() : field.value || ''} />
                            )}
                        />
                        <Controller
                            control={control}
                            name={`caahepinstitutionId`}
                            render={({ field }) => <input type={'hidden'} {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            control={control}
                            name={`caahepprogramId`}
                            render={({ field }) => <input type={'hidden'} {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            control={control}
                            name={`programTitle`}
                            render={({ field }) => <input type={'hidden'} {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            control={control}
                            name={`transferFromProgramId`}
                            render={({ field }) => <input type={'hidden'} {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            control={control}
                            name={`transferFromInstitutionId`}
                            render={({ field }) => <input type={'hidden'} {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            control={control}
                            name={`transferToInstitutionId`}
                            render={({ field }) => <input type={'hidden'} {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            control={control}
                            name={`programProfessionId`}
                            render={({ field }) => <input type={'hidden'} {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            control={control}
                            name={`professionTitle`}
                            render={({ field }) => <input type={'hidden'} {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            control={control}
                            name={`programConcentrationId`}
                            render={({ field }) => <input type={'hidden'} {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            control={control}
                            name={`professionLevelId`}
                            render={({ field }) => <input type={'hidden'} {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            control={control}
                            name={`concentrationTitles`}
                            render={({ field }) => <input type={'hidden'} {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            control={control}
                            name={`levelTitle`}
                            render={({ field }) => <input type={'hidden'} {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            control={control}
                            name={`programAddOnTrackId`}
                            render={({ field }) => <input type={'hidden'} {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            control={control}
                            name={`addOnTrackTitles`}
                            render={({ field }) => <input type={'hidden'} {...field} value={field.value ?? ''} />}
                        />
                        <Row>
                            <Col>
                                <Card className={`bg-dark text-white`}>
                                    <CardBody className={`d-flex flex-column ribbon-box`}>
                                        <div>
                                            {recommendation.isMerged && (
                                                <Ribbon color={'success'}>
                                                    <span>{`This recommendation has been merged`}</span>
                                                </Ribbon>
                                            )}
                                            {!recommendation.isMerged && (
                                                <Ribbon color={'warning'}>
                                                    <span>{`This recommendation has not been merged`}</span>
                                                </Ribbon>
                                            )}
                                        </div>
                                        {recommendation.transferFromProgramId && (
                                            <span className={'card-title text-secondary'} style={{ lineHeight: '2em' }}>
                                                <Badge
                                                    className={`mr-2 text-dark`}
                                                    color={'warning'}
                                                >{`Transfer Sponsorship`}</Badge>
                                                <span>{`This recommendation is for program `}</span>
                                                <span
                                                    className={'font-weight-bold text-white'}
                                                >{`${recommendation.programTitle} `}</span>
                                                {!recommendation.caahepprogramId && (
                                                    <Badge
                                                        className={`text-dark`}
                                                        color={'warning'}
                                                    >{`New Program`}</Badge>
                                                )}
                                                {recommendation.caahepprogramId && (
                                                    <Badge
                                                        className={`text-dark`}
                                                        color={'info'}
                                                    >{`Existing Program`}</Badge>
                                                )}
                                                <span>{` and is transferring sponsorship from `}</span>
                                                <span
                                                    className={'font-weight-bold text-white'}
                                                >{`${transferFromInstitution?.title}`}</span>
                                                <span>{` to `}</span>
                                                <span
                                                    className={'font-weight-bold text-white'}
                                                >{`${recommendation.institutionTitle} `}</span>
                                                {(recommendation.transferToInstitutionId || 0) > 0 && (
                                                    <Badge
                                                        className={`text-dark`}
                                                        color={'info'}
                                                    >{`Existing Institution`}</Badge>
                                                )}
                                                {(recommendation.transferToInstitutionId || 0) === 0 && (
                                                    <Badge
                                                        className={`text-dark`}
                                                        color={'warning'}
                                                    >{`New Institution`}</Badge>
                                                )}
                                            </span>
                                        )}
                                        {!recommendation.transferFromProgramId && (
                                            <span className={'card-title text-secondary'}>
                                                <span>{`This recommendation is for program `}</span>
                                                <span
                                                    className={'font-weight-bold text-white'}
                                                >{`${recommendation.programTitle} `}</span>
                                                {!recommendation.caahepprogramId && (
                                                    <Badge
                                                        className={`text-dark`}
                                                        color={'warning'}
                                                    >{`New Program`}</Badge>
                                                )}
                                                {recommendation.caahepprogramId && (
                                                    <Badge
                                                        className={`text-dark`}
                                                        color={'info'}
                                                    >{`Existing Program`}</Badge>
                                                )}
                                                <span>{` at `}</span>
                                                <span
                                                    className={'font-weight-bold text-white'}
                                                >{`${recommendation.institutionTitle} `}</span>
                                                {+(recommendation.caahepinstitutionId || 0) > 0 && (
                                                    <Badge
                                                        className={`text-dark`}
                                                        color={'info'}
                                                    >{`Existing Institution`}</Badge>
                                                )}
                                                {+(recommendation.caahepinstitutionId || 0) === 0 && (
                                                    <Badge
                                                        className={`text-dark`}
                                                        color={'warning'}
                                                    >{`New Institution`}</Badge>
                                                )}
                                            </span>
                                        )}
                                        {createdByInfo?.name && (
                                            <Label className={`font-italic text-secondary`}>
                                                {`Created By: ${createdByInfo.name}`}
                                                {recommendation.createdOn &&
                                                    ` on ${moment
                                                        .utc(recommendation.createdOn)
                                                        .local()
                                                        .format('MM/DD/YYYY hh:mm A')}`}
                                            </Label>
                                        )}
                                        {modifiedByInfo?.name && (
                                            <Label className={`font-italic text-secondary`}>
                                                {`Last Edited By: ${modifiedByInfo.name}`}
                                                {recommendation.modifiedOn &&
                                                    ` on ${moment
                                                        .utc(recommendation.modifiedOn)
                                                        .local()
                                                        .format('MM/DD/YYYY hh:mm A')}`}
                                            </Label>
                                        )}
                                        {mergedByInfo?.name && (
                                            <Label className={`font-italic text-secondary`}>
                                                {`Merged By: ${mergedByInfo.name}`}
                                                {recommendation.mergedOn &&
                                                    ` on ${moment
                                                        .utc(recommendation.mergedOn)
                                                        .local()
                                                        .format('MM/DD/YYYY hh:mm A')}`}
                                            </Label>
                                        )}
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={12}>
                                <Card>
                                    <CardHeader className={'bg-white'}>
                                        <h4>
                                            <div className={'d-flex align-items-center'}>
                                                <span className={'mr-3'}>{`Institution Details`}</span>
                                                {canMerge && (
                                                    <div className={'custom-control custom-checkbox mr-2'}>
                                                        <Controller
                                                            control={control}
                                                            name={'isInstitutionSectionComplete'}
                                                            render={({ field }) => (
                                                                <Input
                                                                    type={'checkbox'}
                                                                    className={'custom-control-input'}
                                                                    id={'instSection'}
                                                                    name={field.name}
                                                                    onBlur={field.onBlur}
                                                                    ref={field.ref}
                                                                    checked={field.value}
                                                                    onChange={(e) => field.onChange(e.target.checked)}
                                                                />
                                                            )}
                                                        />
                                                        <Label
                                                            className={'custom-control-label font-weight-normal'}
                                                            for="instSection"
                                                        >{`Accept`}</Label>
                                                    </div>
                                                )}
                                            </div>
                                        </h4>
                                    </CardHeader>
                                    <CardBody>
                                        <Row>
                                            <Col xs={12} lg={6}>
                                                <FormGroup>
                                                    <Label>
                                                        <span>{`Institution ID`}</span>
                                                        {canMerge && (
                                                            <Button
                                                                className={'ml-3 btn-sm'}
                                                                type={'button'}
                                                                color={'primary'}
                                                                onClick={() => toggleShowChangeInst()}
                                                            >
                                                                <span>{`Change Institution`}</span>
                                                            </Button>
                                                        )}
                                                    </Label>
                                                    {recommendation.transferFromProgramId && (
                                                        <Input
                                                            type={'text'}
                                                            readOnly={true}
                                                            disabled={true}
                                                            value={
                                                                recommendation.transferToInstitutionId ||
                                                                'New Institution'
                                                            }
                                                        />
                                                    )}
                                                    {!recommendation.transferFromProgramId && (
                                                        <Input
                                                            type={'text'}
                                                            readOnly={true}
                                                            disabled={true}
                                                            value={
                                                                recommendation.caahepinstitutionId || 'New Institution'
                                                            }
                                                        />
                                                    )}
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Institution Title`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'institutionTitle'}
                                                        render={({ field }) => (
                                                            <Input
                                                                type={'text'}
                                                                readOnly={cannotEditInstitution}
                                                                disabled={cannotEditInstitution}
                                                                {...field}
                                                            />
                                                        )}
                                                    />
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Institution Type`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'institutionTypeId'}
                                                        render={({ field }) => (
                                                            <Input
                                                                type={'select'}
                                                                readOnly={cannotEditInstitution}
                                                                disabled={cannotEditInstitution}
                                                                {...field}
                                                            >
                                                                <option value={''}>{`Select...`}</option>
                                                                {institutionTypes &&
                                                                    institutionTypes.map((it) => (
                                                                        <option
                                                                            key={it.institutionTypeId}
                                                                            value={it.institutionTypeId}
                                                                        >
                                                                            {it.title}
                                                                        </option>
                                                                    ))}
                                                            </Input>
                                                        )}
                                                    />
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Auspice`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'institutionAuspiceId'}
                                                        render={({ field }) => (
                                                            <Input
                                                                type={'select'}
                                                                readOnly={cannotEditInstitution}
                                                                disabled={cannotEditInstitution}
                                                                {...field}
                                                            >
                                                                <option value={''}>{`Select...`}</option>
                                                                {auspices &&
                                                                    auspices.map((a) => (
                                                                        <option key={a.auspiceID} value={a.auspiceID}>
                                                                            {a.title}
                                                                        </option>
                                                                    ))}
                                                            </Input>
                                                        )}
                                                    />
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Institutional Accreditor`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'institutionalAccreditorId'}
                                                        render={({ field }) => (
                                                            <Input
                                                                type={'select'}
                                                                {...field}
                                                                invalid={!!errors.institutionalAccreditorId}
                                                            >
                                                                <option value={''}>{`Select...`}</option>
                                                                {accreditors &&
                                                                    accreditors.map((a) => (
                                                                        <option
                                                                            key={a.institutionalAccreditorId}
                                                                            value={a.institutionalAccreditorId}
                                                                        >
                                                                            {a.title}
                                                                        </option>
                                                                    ))}
                                                            </Input>
                                                        )}
                                                    />
                                                    {errors.institutionalAccreditorId && (
                                                        <small className="text-danger">
                                                            {errors.institutionalAccreditorId.message?.toString() || 'Institutional Accreditor is required'}
                                                        </small>
                                                    )}
                                                </FormGroup>
                                                <Row>
                                                    <Col xs={12} lg={9}>
                                                        <FormGroup>
                                                            <Label>{`Phone`}</Label>
                                                            <Controller
                                                                control={control}
                                                                name={'institutionPhone'}
                                                                render={({ field }) => (
                                                                    <Input
                                                                        type={'text'}
                                                                        readOnly={cannotEditInstitution}
                                                                        disabled={cannotEditInstitution}
                                                                        {...field}
                                                                    />
                                                                )}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col xs={12} lg={3}>
                                                        <FormGroup>
                                                            <Label>{`Extension`}</Label>
                                                            <Controller
                                                                control={control}
                                                                name={'institutionExtension'}
                                                                render={({ field }) => (
                                                                    <Input
                                                                        type={'text'}
                                                                        readOnly={cannotEditInstitution}
                                                                        disabled={cannotEditInstitution}
                                                                        {...field}
                                                                    />
                                                                )}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                </Row>
                                                <FormGroup>
                                                    <Label>{`Website`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'institutionWebSite'}
                                                        render={({ field }) => (
                                                            <Input
                                                                type={'text'}
                                                                readOnly={cannotEditInstitution}
                                                                disabled={cannotEditInstitution}
                                                                {...field}
                                                            />
                                                        )}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col xs={12} lg={6}>
                                                <FormGroup>
                                                    <Label>{`Address`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'institutionAddress'}
                                                        render={({ field }) => (
                                                            <Input
                                                                type={'text'}
                                                                readOnly={cannotEditInstitution}
                                                                disabled={cannotEditInstitution}
                                                                {...field}
                                                            />
                                                        )}
                                                    />
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Address 2`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'institutionAddress2'}
                                                        render={({ field }) => (
                                                            <Input
                                                                type={'text'}
                                                                readOnly={cannotEditInstitution}
                                                                disabled={cannotEditInstitution}
                                                                {...field}
                                                            />
                                                        )}
                                                    />
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Address 3`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'institutionAddress3'}
                                                        render={({ field }) => (
                                                            <Input
                                                                type={'text'}
                                                                readOnly={cannotEditInstitution}
                                                                disabled={cannotEditInstitution}
                                                                {...field}
                                                            />
                                                        )}
                                                    />
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Address 4`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'institutionAddress4'}
                                                        render={({ field }) => (
                                                            <Input
                                                                type={'text'}
                                                                readOnly={cannotEditInstitution}
                                                                disabled={cannotEditInstitution}
                                                                {...field}
                                                            />
                                                        )}
                                                    />
                                                </FormGroup>
                                                <Row>
                                                    <Col xs={12} lg={6}>
                                                        <FormGroup>
                                                            <Label>{`City`}</Label>
                                                            <Controller
                                                                control={control}
                                                                name={'institutionCity'}
                                                                render={({ field }) => (
                                                                    <Input
                                                                        type={'text'}
                                                                        readOnly={cannotEditInstitution}
                                                                        disabled={cannotEditInstitution}
                                                                        {...field}
                                                                    />
                                                                )}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col xs={12} lg={6}>
                                                        <FormGroup>
                                                            <Label>{`State`}</Label>
                                                            {recommendation.institutionCountry ===
                                                                'United States of America' &&
                                                                States && (
                                                                    <Controller
                                                                        control={control}
                                                                        name={'institutionState'}
                                                                        render={({ field }) => (
                                                                            <Input
                                                                                type={'select'}
                                                                                readOnly={cannotEditInstitution}
                                                                                disabled={cannotEditInstitution}
                                                                                {...field}
                                                                            >
                                                                                <option value={undefined}></option>
                                                                                {States.map((s) => (
                                                                                    <option
                                                                                        key={s.abbreviation}
                                                                                        value={s.abbreviation}
                                                                                    >
                                                                                        {s.abbreviation}
                                                                                    </option>
                                                                                ))}
                                                                            </Input>
                                                                        )}
                                                                    />
                                                                )}
                                                            {recommendation.institutionCountry !==
                                                                'United States of America' && (
                                                                <Controller
                                                                    control={control}
                                                                    name={'institutionState'}
                                                                    render={({ field }) => (
                                                                        <Input
                                                                            type={'text'}
                                                                            readOnly={cannotEditInstitution}
                                                                            disabled={cannotEditInstitution}
                                                                            {...field}
                                                                        />
                                                                    )}
                                                                />
                                                            )}
                                                        </FormGroup>
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col xs={12} lg={6}>
                                                        <FormGroup>
                                                            <Label>{`Zip / Postal Code`}</Label>
                                                            <Controller
                                                                control={control}
                                                                name={'institutionZip'}
                                                                render={({ field }) => (
                                                                    <Input
                                                                        type={'text'}
                                                                        readOnly={cannotEditInstitution}
                                                                        disabled={cannotEditInstitution}
                                                                        {...field}
                                                                    />
                                                                )}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col xs={12} lg={6}>
                                                        <FormGroup>
                                                            <Label>{`Country`}</Label>
                                                            <Controller
                                                                control={control}
                                                                name={'institutionCountry'}
                                                                render={({ field }) => (
                                                                    <Input
                                                                        type={'select'}
                                                                        readOnly={cannotEditInstitution}
                                                                        disabled={cannotEditInstitution}
                                                                        {...field}
                                                                    >
                                                                        <option value={undefined}></option>
                                                                        {Countries.map((c) => (
                                                                            <option key={c} value={c}>
                                                                                {c}
                                                                            </option>
                                                                        ))}
                                                                    </Input>
                                                                )}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col xs={12}>
                                <Card>
                                    <CardHeader className={'bg-white'}>
                                        <h4>
                                            <div className={'d-flex align-items-center'}>
                                                <span className={'mr-3'}>{`Program Details`}</span>
                                                <div className={'custom-control custom-checkbox mr-2'}>
                                                    <Input
                                                        type={'checkbox'}
                                                        className={'custom-control-input'}
                                                        id={'useInstitutionAddress'}
                                                        checked={useInstitutionAddress}
                                                        onChange={(e) => {
                                                            setUseInstitutionAddress(e.target.checked);
                                                        }}
                                                    />
                                                    <Label
                                                        className={'custom-control-label font-weight-normal'}
                                                        for="useInstitutionAddress"
                                                    >{`Use Institution Address`}</Label>
                                                </div>
                                                {canMerge && (
                                                    <div className={'custom-control custom-checkbox mr-2'}>
                                                        <Controller
                                                            control={control}
                                                            name={'isProgramSectionComplete'}
                                                            render={({ field }) => (
                                                                <Input
                                                                    type={'checkbox'}
                                                                    className={'custom-control-input'}
                                                                    id={'progSection'}
                                                                    checked={field.value || false}
                                                                    onChange={(e) => field.onChange(e.target.checked)}
                                                                />
                                                            )}
                                                        />
                                                        <Label
                                                            className={'custom-control-label font-weight-normal'}
                                                            for="progSection"
                                                        >{`Accept`}</Label>
                                                    </div>
                                                )}
                                            </div>
                                        </h4>
                                    </CardHeader>
                                    <CardBody>
                                        <Row>
                                            <Col xs={12} lg={6}>
                                                <FormGroup>
                                                    <Label>{`Profession`}</Label>
                                                    <div>
                                                        <span>{recommendation.professionTitle}</span>
                                                    </div>
                                                </FormGroup>
                                                {recommendation.levelTitle && (
                                                    <FormGroup>
                                                        <Label>{`Level`}</Label>
                                                        <div>
                                                            <span>{recommendation.levelTitle}</span>
                                                        </div>
                                                    </FormGroup>
                                                )}
                                                <FormGroup>
                                                    <Label>{`Program Name`}</Label>
                                                    <div>
                                                        <Controller
                                                            control={control}
                                                            name={'programName'}
                                                            render={({ field }) => (
                                                                <Input
                                                                    type={'text'}
                                                                    className={'form-control'}
                                                                    disabled={isExistingProgramForNonAdmin}
                                                                    {...field}
                                                                />
                                                            )}
                                                        />{' '}
                                                    </div>
                                                </FormGroup>
                                                {/* Show Concentrations section if profession has concentrations */}
                                                {profession?.professionsConcentrations && profession.professionsConcentrations.length > 0 && (
                                                    <FormGroup>
                                                        <Label>{`Concentrations`}</Label>
                                                        {extensions && extensions.filter(e => e.concentrationId && shouldShowExtension(e)).length > 0 && (
                                                            <Row>
                                                                {extensions.filter(e => e.concentrationId && shouldShowExtension(e)).map((ext) => (
                                                                    <Col key={ext.id} xs={12} lg={12} className="mb-2">
                                                                        <Card>
                                                                            <CardBody className="py-2">
                                                                                <div className="d-flex justify-content-between align-items-center">
                                                                                    <div className="d-flex align-items-center">
                                                                                        <span className="mr-2">
                                                                                            {ext.concentrationTitle}
                                                                                        </span>
                                                                                        <Badge color={getExtensionStatusColor(ext)}>
                                                                                            {getExtensionStatusText(ext)}
                                                                                        </Badge>
                                                                                    </div>
                                                                                    {ext.isDeleted && hasBeenMerged() && (
                                                                                        <span className="text-danger ml-2">
                                                                                            (Removed)
                                                                                        </span>
                                                                                    )}
                                                                                    {!hasBeenMerged() && ext.currentStatusId !== 3 && ext.activeStatus !== 3 && (
                                                                                        <Button
                                                                                            type={'button'}
                                                                                            className={'btn-sm'}
                                                                                            color={ext.isDeleted ? 'info' : 'danger'}
                                                                                            onClick={() => toggleExtension(ext)}
                                                                                        >
                                                                                            <i className={ext.isDeleted ? 'mdi mdi-history' : 'mdi mdi-delete'} />
                                                                                            <span className={'ml-1'}>
                                                                                                {ext.isDeleted ? 'Restore' : 'Remove'}
                                                                                            </span>
                                                                                        </Button>
                                                                                    )}
                                                                                </div>
                                                                            </CardBody>
                                                                        </Card>
                                                                    </Col>
                                                                ))}
                                                            </Row>
                                                        )}
                                                        {(!extensions || extensions.filter(e => e.concentrationId && shouldShowExtension(e)).length === 0) && (
                                                            <div>
                                                                <span className="text-muted">No concentrations</span>
                                                            </div>
                                                        )}
                                                        {!hasBeenMerged() && (
                                                            <div className="mt-2">
                                                                <Button
                                                                    className={'btn-sm'}
                                                                    color={'primary'}
                                                                    onClick={() => {
                                                                        setEditExtension({
                                                                            id: 0,
                                                                            recommendationId: recId,
                                                                            extensionTypeId: 2, // Concentration
                                                                            isDeleted: false,
                                                                            currentStatusId: 1,
                                                                            currentStatusEffectiveDate: recommendation.meetingDate || new Date(),
                                                                            dateOfInitialApproval: recommendation.meetingDate || new Date(),
                                                                        });
                                                                        toggleShowExtensionModal();
                                                                    }}
                                                                >
                                                                    <i className={'mdi mdi-plus'} />
                                                                    <span className={'ml-1'}>Add Concentration</span>
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </FormGroup>
                                                )}

                                                {/* Show Add-On Tracks section if profession has add-ons */}
                                                {profession?.professionAddOnTracks && profession.professionAddOnTracks.length > 0 && (
                                                    <FormGroup>
                                                        <Label>{`Add-On Tracks`}</Label>
                                                        {extensions && extensions.filter(e => e.addOnTrackId && shouldShowExtension(e)).length > 0 && (
                                                            <Row>
                                                                {extensions.filter(e => e.addOnTrackId && shouldShowExtension(e)).map((ext) => (
                                                                    <Col key={ext.id} xs={12} lg={12} className="mb-2">
                                                                        <Card>
                                                                            <CardBody className="py-2">
                                                                                <div className="d-flex justify-content-between align-items-center">
                                                                                    <div className="d-flex align-items-center">
                                                                                        <span className="mr-2">
                                                                                            {ext.addOnTrackTitle}
                                                                                        </span>
                                                                                        <Badge color={getExtensionStatusColor(ext)}>
                                                                                            {getExtensionStatusText(ext)}
                                                                                        </Badge>
                                                                                    </div>
                                                                                    {ext.isDeleted && hasBeenMerged() && (
                                                                                        <span className="text-danger ml-2">
                                                                                            (Removed)
                                                                                        </span>
                                                                                    )}
                                                                                    {!hasBeenMerged() && ext.currentStatusId !== 3 && ext.activeStatus !== 3 && (
                                                                                        <Button
                                                                                            type={'button'}
                                                                                            className={'btn-sm'}
                                                                                            color={ext.isDeleted ? 'info' : 'danger'}
                                                                                            onClick={() => toggleExtension(ext)}
                                                                                        >
                                                                                            <i className={ext.isDeleted ? 'mdi mdi-history' : 'mdi mdi-delete'} />
                                                                                            <span className={'ml-1'}>
                                                                                                {ext.isDeleted ? 'Restore' : 'Remove'}
                                                                                            </span>
                                                                                        </Button>
                                                                                    )}
                                                                                </div>
                                                                            </CardBody>
                                                                        </Card>
                                                                    </Col>
                                                                ))}
                                                            </Row>
                                                        )}
                                                        {(!extensions || extensions.filter(e => e.addOnTrackId && shouldShowExtension(e)).length === 0) && (
                                                            <div>
                                                                <span className="text-muted">No add-on tracks</span>
                                                            </div>
                                                        )}
                                                        {!hasBeenMerged() && (
                                                            <div className="mt-2">
                                                                <Button
                                                                    className={'btn-sm'}
                                                                    color={'primary'}
                                                                    onClick={() => {
                                                                        setEditExtension({
                                                                            id: 0,
                                                                            recommendationId: recId,
                                                                            extensionTypeId: 1, // Add-On
                                                                            isDeleted: false,
                                                                            currentStatusId: 1,
                                                                            currentStatusEffectiveDate: recommendation.meetingDate || new Date(),
                                                                            dateOfInitialApproval: recommendation.meetingDate || new Date(),
                                                                        });
                                                                        toggleShowExtensionModal();
                                                                    }}
                                                                >
                                                                    <i className={'mdi mdi-plus'} />
                                                                    <span className={'ml-1'}>Add Add-On Track</span>
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </FormGroup>
                                                )}
                                                {!!profession && (
                                                    <FormGroup>
                                                        <Label>{`Award / Degree`}</Label>
                                                        <Controller
                                                            control={control}
                                                            name={'degreeTypeId'}
                                                            render={({ field }) => (
                                                                <Input
                                                                    type={'select'}
                                                                    {...field}
                                                                    value={String(field.value || '')}
                                                                    onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                                                                    disabled={isExistingProgramForNonAdmin}
                                                                >
                                                                    <option value={''}>{`Select...`}</option>
                                                                    {DegreeTypes.map((a) => (
                                                                        <option key={a} value={String(DegreeType[a])}>
                                                                            {a}
                                                                        </option>
                                                                    ))}
                                                                </Input>
                                                            )}
                                                        />
                                                        {errors?.degreeTypeId?.message && (
                                                            <div>
                                                                <span className={'text-danger'}>
                                                                    {errors.degreeTypeId.message}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </FormGroup>
                                                )}
                                                <FormGroup>
                                                    <Label>{`Date First Accredited`}</Label>
                                                    {canMerge && (
                                                        <Controller
                                                            control={control}
                                                            name={'programDateFirstAccredited'}
                                                            render={({ field }) => {
                                                                const dateValue = field.value ? (field.value instanceof Date ? field.value.toISOString().split('T')[0] : typeof field.value === 'string' ? field.value.split('T')[0] : '') : '';
                                                                return <Input type={'date'} {...field} value={dateValue} onChange={(e) => field.onChange(e.target.value)} />;
                                                            }}
                                                        />
                                                    )}
                                                    {!canMerge && (
                                                        <>
                                                            <Controller
                                                                control={control}
                                                                name={'programDateFirstAccredited'}
                                                                render={({ field }) => (
                                                                    <Input type={'hidden'} {...field} />
                                                                )}
                                                            />
                                                            <span className={'d-block'}>
                                                                {recommendation.programDateFirstAccredited
                                                                    ? moment
                                                                          .utc(recommendation.programDateFirstAccredited)
                                                                          .format('MM/DD/YYYY')
                                                                    : ''}
                                                            </span>
                                                        </>
                                                    )}
                                                    {errors?.programDateFirstAccredited?.message && (
                                                        <div className={'mt-1'}>
                                                            <span className={'text-danger'}>
                                                                {errors.programDateFirstAccredited.message}
                                                            </span>
                                                        </div>
                                                    )}
                                                </FormGroup>
                                                <FormGroup>
                                                    <div className={'d-flex align-items-center'}>
                                                        <div className={'custom-control custom-checkbox mr-4'}>
                                                            <Controller
                                                                control={control}
                                                                name={'programDistance'}
                                                                render={({ field }) => (
                                                                    <Input
                                                                        type={'checkbox'}
                                                                        className={'custom-control-input'}
                                                                        id={'distance'}
                                                                        checked={field.value || false}
                                                                        onChange={(e) =>
                                                                            field.onChange(e.target.checked)
                                                                        }
                                                                    />
                                                                )}
                                                            />
                                                            <Label
                                                                className={'custom-control-label'}
                                                                for="distance"
                                                            >{`Distance?`}</Label>
                                                        </div>
                                                        <div className={'custom-control custom-checkbox'}>
                                                            <Controller
                                                                control={control}
                                                                name={'programMilitaryPersonnelOnly'}
                                                                render={({ field }) => (
                                                                    <Input
                                                                        type={'checkbox'}
                                                                        className={'custom-control-input'}
                                                                        id={'military'}
                                                                        checked={field.value || false}
                                                                        onChange={(e) =>
                                                                            field.onChange(e.target.checked)
                                                                        }
                                                                    />
                                                                )}
                                                            />
                                                            <Label
                                                                className={'custom-control-label'}
                                                                for="military"
                                                            >{`Military?`}</Label>
                                                        </div>
                                                    </div>
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Program Website`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'programWebSite'}
                                                        render={({ field }) => (
                                                            <Input
                                                                type={'text'}
                                                                className={'form-control'}
                                                                {...field}
                                                            />
                                                        )}
                                                    />
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Outcomes Url`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'programOutcomesUrl'}
                                                        render={({ field }) => (
                                                            <Input
                                                                type={'text'}
                                                                className={'form-control'}
                                                                {...field}
                                                            />
                                                        )}
                                                    />
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Satellites`}</Label>
                                                    {satellites && satellites.length > 0 && (
                                                        <Row>
                                                            {satellites.map((s) => (
                                                                <Col key={s.recommendationSatelliteId} xs={12} lg={6}>
                                                                    <Card>
                                                                        <CardBody>
                                                                            <span>{`${s.title}${
                                                                                s.city || s.state
                                                                                    ? ` (${s.city || ''}${
                                                                                          s.city
                                                                                              ? `, ${s.state}`
                                                                                              : `${s.state || ''}`
                                                                                      })`
                                                                                    : ''
                                                                            }`}</span>
                                                                            {s.isActive === false && (
                                                                                <Alert
                                                                                    color={'danger'}
                                                                                    className={'text-center mt-2 mb-0'}
                                                                                    transition={{ timeout: 0 }}
                                                                                >
                                                                                    <span>{`Will be removed when the recommendation is merged.`}</span>
                                                                                </Alert>
                                                                            )}
                                                                        </CardBody>
                                                                        {!hasBeenMerged() && (
                                                                            <CardFooter className={'p-0'}>
                                                                                <ButtonGroup className={'d-flex'}>
                                                                                    {s.isActive === true && (
                                                                                        <>
                                                                                            <Button
                                                                                                type={'button'}
                                                                                                className={'btn-sm'}
                                                                                                color={'white'}
                                                                                                onClick={() => {
                                                                                                    setEditSatellite({
                                                                                                        ...s,
                                                                                                    });
                                                                                                    toggleShowSatellite();
                                                                                                }}
                                                                                            >
                                                                                                <i
                                                                                                    className={
                                                                                                        'mdi mdi-pencil'
                                                                                                    }
                                                                                                />
                                                                                                <span
                                                                                                    className={'ml-1'}
                                                                                                >{`Edit`}</span>
                                                                                            </Button>
                                                                                            <Button
                                                                                                type={'button'}
                                                                                                className={'btn-sm'}
                                                                                                color={'white'}
                                                                                                onClick={() =>
                                                                                                    toggleSatellite(s)
                                                                                                }
                                                                                            >
                                                                                                <i
                                                                                                    className={
                                                                                                        'mdi mdi-delete'
                                                                                                    }
                                                                                                />
                                                                                                <span
                                                                                                    className={'ml-1'}
                                                                                                >{`Remove`}</span>
                                                                                            </Button>
                                                                                        </>
                                                                                    )}
                                                                                </ButtonGroup>
                                                                                {s.isActive === false && (
                                                                                    <Button
                                                                                        type={'button'}
                                                                                        color={'white'}
                                                                                        className={'btn-sm w-100'}
                                                                                        onClick={() =>
                                                                                            toggleSatellite(s)
                                                                                        }
                                                                                    >
                                                                                        <i
                                                                                            className={
                                                                                                'mdi mdi-history'
                                                                                            }
                                                                                        />
                                                                                        <span
                                                                                            className={'ml-1'}
                                                                                        >{`Restore`}</span>
                                                                                    </Button>
                                                                                )}
                                                                            </CardFooter>
                                                                        )}
                                                                    </Card>
                                                                </Col>
                                                            ))}
                                                        </Row>
                                                    )}
                                                    <div className={'d-flex flex-column'}>
                                                        {satellites && satellites.length === 0 && (
                                                            <div>
                                                                <Label>{`No Satellites`}</Label>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {!hasBeenMerged() && (
                                                        <Row>
                                                            <Col xs={12}>
                                                                <Button
                                                                    className={'btn-sm w-100'}
                                                                    color={'primary'}
                                                                    onClick={() => {
                                                                        setEditSatellite({
                                                                            isActive: true,
                                                                            recommendationId: recId,
                                                                            recommendationSatelliteId: 0,
                                                                        });
                                                                        toggleShowSatellite();
                                                                    }}
                                                                >
                                                                    <i className={'mdi mdi-plus'} />
                                                                    <span className={'ml-1'}>{`New Satellite`}</span>
                                                                </Button>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                </FormGroup>
                                            </Col>
                                            <Col xs={12} lg={6}>
                                                <FormGroup>
                                                    <Label>{`Address`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'programAddress'}
                                                        render={({ field }) => <Input type={'text'} {...field} value={field.value ?? ''} />}
                                                    />
                                                    {errors?.programAddress?.message && (
                                                        <div className={'mt-1'}>
                                                            <span className={'text-danger'}>
                                                                {errors.programAddress.message}
                                                            </span>
                                                        </div>
                                                    )}
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Address 2`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'programAddress2'}
                                                        render={({ field }) => <Input type={'text'} {...field} value={field.value ?? ''} />}
                                                    />
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Address 3`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'programAddress3'}
                                                        render={({ field }) => <Input type={'text'} {...field} value={field.value ?? ''} />}
                                                    />
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Address 4`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'programAddress4'}
                                                        render={({ field }) => <Input type={'text'} {...field} value={field.value ?? ''} />}
                                                    />
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`City`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'programCity'}
                                                        render={({ field }) => <Input type={'text'} {...field} value={field.value ?? ''} />}
                                                    />
                                                    {errors?.programCity?.message && (
                                                        <div className={'mt-1'}>
                                                            <span className={'text-danger'}>
                                                                {errors.programCity.message}
                                                            </span>
                                                        </div>
                                                    )}
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`State`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'programState'}
                                                        render={({ field }) => (
                                                            <Input type={'select'} {...field}>
                                                                <option value={undefined}></option>
                                                                {States.map((s) => (
                                                                    <option key={s.abbreviation} value={s.abbreviation}>
                                                                        {s.abbreviation}
                                                                    </option>
                                                                ))}
                                                            </Input>
                                                        )}
                                                    />
                                                    {errors?.programState?.message && (
                                                        <div className={'mt-1'}>
                                                            <span className={'text-danger'}>
                                                                {errors.programState.message}
                                                            </span>
                                                        </div>
                                                    )}
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Zip / Postal Code`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'programZip'}
                                                        render={({ field }) => <Input type={'text'} {...field} value={field.value ?? ''} />}
                                                    />
                                                    {errors?.programZip?.message && (
                                                        <div className={'mt-1'}>
                                                            <span className={'text-danger'}>
                                                                {errors.programZip.message}
                                                            </span>
                                                        </div>
                                                    )}
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Country`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'programCountry'}
                                                        render={({ field }) => (
                                                            <Input type={'select'} {...field}>
                                                                <option value={''}>{`Select...`}</option>
                                                                {Countries.map((c) => (
                                                                    <option key={c} value={c}>
                                                                        {c}
                                                                    </option>
                                                                ))}
                                                            </Input>
                                                        )}
                                                    />
                                                    {errors?.programCountry?.message && (
                                                        <div className={'mt-1'}>
                                                            <span className={'text-danger'}>
                                                                {errors.programCountry.message}
                                                            </span>
                                                        </div>
                                                    )}
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>
                            {(recommendation.caahepprogramId || 0) > 0 && (
                                <Col xs={12} lg={6}>
                                    <Card>
                                        <CardHeader className={'bg-white'}>
                                            <h4>{`Program Status History`}</h4>
                                        </CardHeader>
                                        <div className={'table-responsive'}>
                                            <table className={'table mb-0'}>
                                                <thead>
                                                    <tr>
                                                        <th>{`Status`}</th>
                                                        <th>{`Effective Date`}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {statusHistory &&
                                                        statusHistory.map((sh) => (
                                                            <tr key={sh.historyId}>
                                                                <td>{ProgramStatus[sh.statusId]}</td>
                                                                <td>{moment(sh.effectiveDate).format('MM/DD/YYYY')}</td>
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </Card>
                                </Col>
                            )}
                            <Col xs={12} lg={6}>
                                <Card>
                                    <CardHeader className={'bg-white'}>
                                        <h4>
                                            <div className={'d-flex align-items-center'}>
                                                <span className={'mr-3'}>{`Recommendation Details`}</span>
                                                {canMerge && (
                                                    <div className={'custom-control custom-checkbox mr-2'}>
                                                        <Controller
                                                            control={control}
                                                            name={'isRecommendationSectionComplete'}
                                                            render={({ field }) => (
                                                                <Input
                                                                    type={'checkbox'}
                                                                    className={'custom-control-input'}
                                                                    id={'recSection'}
                                                                    checked={field.value || false}
                                                                    onChange={(e) => field.onChange(e.target.checked)}
                                                                />
                                                            )}
                                                        />
                                                        <Label
                                                            className={'custom-control-label font-weight-normal'}
                                                            for="recSection"
                                                        >{`Accept`}</Label>
                                                    </div>
                                                )}
                                            </div>
                                        </h4>
                                    </CardHeader>
                                    <CardBody>
                                        <Row>
                                            <Col xs={12} lg={6}>
                                                <FormGroup>
                                                    <Label>{`Standards Version`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'professionStandardId'}
                                                        render={({ field }) => (
                                                            <Input type={'select'} {...field} value={field.value ?? ''}>
                                                                <option value="">Select...</option>
                                                                {standards &&
                                                                    standards.map((s) => (
                                                                        <option
                                                                            key={s.professionStandardId}
                                                                            value={s.professionStandardId}
                                                                        >
                                                                            {s.standardYear}
                                                                        </option>
                                                                    ))}
                                                            </Input>
                                                        )}
                                                    />
                                                    {errors?.professionStandardId?.message && (
                                                        <span className={'text-danger'}>{errors.professionStandardId.message}</span>
                                                    )}
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Recommended Status`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'statusId'}
                                                        render={({ field }) => (
                                                            <Input type={'select'} {...field} value={field.value ?? ''}>
                                                                <option value="">Select...</option>
                                                                {programStatuses.map((s) => (
                                                                    <option key={s} value={s}>
                                                                        {ProgramStatus[s]}
                                                                    </option>
                                                                ))}
                                                            </Input>
                                                        )}
                                                    />
                                                    {errors?.statusId?.message && (
                                                        <span className={'text-danger'}>{errors.statusId.message}</span>
                                                    )}
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`CAAHEP Meeting`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'meetingDateId'}
                                                        render={({ field }) => (
                                                            <Input
                                                                type={'select'}
                                                                {...field}
                                                                disabled={!canMerge}
                                                                onChange={(e) => {
                                                                    field.onChange(e);
                                                                    setEffectiveDate(
                                                                        dates?.find(
                                                                            (d) => d.meetingDateId === +e.target.value,
                                                                        )?.meetingDate,
                                                                    );
                                                                }}
                                                            >
                                                                {dates &&
                                                                    dates.map((d) => (
                                                                        <option
                                                                            key={d.meetingDateId}
                                                                            value={d.meetingDateId}
                                                                        >
                                                                            {moment(d.meetingDate).format('MM/DD/YYYY')}
                                                                        </option>
                                                                    ))}
                                                            </Input>
                                                        )}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col xs={12} lg={6}>
                                                <FormGroup>
                                                    <Label>{`Effective Date`}</Label>
                                                    {canMerge && (
                                                        <Controller
                                                            control={control}
                                                            name={'effectiveDate'}
                                                            render={({ field }) => {
                                                                const dateValue = field.value ? (field.value instanceof Date ? field.value.toISOString().split('T')[0] : typeof field.value === 'string' ? field.value.split('T')[0] : '') : '';
                                                                return <Input type={'date'} {...field} value={dateValue} onChange={(e) => field.onChange(e.target.value)} />;
                                                            }}
                                                        />
                                                    )}
                                                    {!canMerge && (
                                                        <>
                                                            <Controller
                                                                control={control}
                                                                name={'effectiveDate'}
                                                                render={({ field }) => (
                                                                    <Input type={'hidden'} {...field} />
                                                                )}
                                                            />
                                                            <span className={'d-block'}>
                                                                {moment
                                                                    .utc(recommendation.effectiveDate)
                                                                    .format('MM/DD/YYYY')}
                                                            </span>
                                                        </>
                                                    )}
                                                    {errors?.effectiveDate?.message && (
                                                        <div className={'mt-1'}>
                                                            <span className={'text-danger'}>
                                                                {errors.effectiveDate.message}
                                                            </span>
                                                        </div>
                                                    )}
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Next Full Evaluation`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'nextFullEval'}
                                                        render={({ field }) => {
                                                            const dateValue = field.value ? (field.value instanceof Date ? field.value.toISOString().split('T')[0] : typeof field.value === 'string' ? field.value.split('T')[0] : '') : '';
                                                            return <Input type={'date'} {...field} value={dateValue} onChange={(e) => field.onChange(e.target.value)} />;
                                                        }}
                                                    />
                                                    {errors?.nextFullEval?.message && (
                                                        <div className={'mt-1'}>
                                                            <span className={'text-danger'}>
                                                                {errors.nextFullEval.message}
                                                            </span>
                                                        </div>
                                                    )}
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col xs={12} lg={6}>
                                <Card>
                                    <CardHeader className={'bg-white'}>
                                        <h4>
                                            <div className={'d-flex justify-content-between'}>
                                                <div className={'d-flex align-items-center'}>
                                                    <span className={'mr-3'}>{`Citations`}</span>
                                                    {canMerge && (
                                                        <div className={'custom-control custom-checkbox mr-2'}>
                                                            <Controller
                                                                control={control}
                                                                name={'isCitationSectionComplete'}
                                                                render={({ field }) => (
                                                                    <Input
                                                                        type={'checkbox'}
                                                                        className={'custom-control-input'}
                                                                        id={'citSection'}
                                                                        checked={field.value || false}
                                                                        onChange={(e) =>
                                                                            field.onChange(e.target.checked)
                                                                        }
                                                                    />
                                                                )}
                                                            />
                                                            <Label
                                                                className={'custom-control-label font-weight-normal'}
                                                                for="citSection"
                                                            >{`Accept`}</Label>
                                                        </div>
                                                    )}
                                                </div>
                                                {(recommendation.isMerged === false ||
                                                    recommendation.isMerged.toString() === 'false') && (
                                                    <Button
                                                        type={'button'}
                                                        color={'primary'}
                                                        onClick={() => newCitation()}
                                                    >
                                                        {`New Citation`}
                                                    </Button>
                                                )}
                                            </div>
                                        </h4>
                                    </CardHeader>
                                    <CardBody>
                                        <Row>
                                            <Col xs={12} md={6} xl={4}>
                                                <FormGroup>
                                                    <Label>{`Progress Report Due Date`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'prdue'}
                                                        render={({ field }) => {
                                                            const dateValue = field.value ? (field.value instanceof Date ? field.value.toISOString().split('T')[0] : typeof field.value === 'string' ? field.value.split('T')[0] : '') : '';
                                                            return <Input type={'date'} {...field} value={dateValue} onChange={(e) => field.onChange(e.target.value)} />;
                                                        }}
                                                    />
                                                    {errors?.prdue?.message && (
                                                        <div className={'mt-1'}>
                                                            <span className={'text-danger'}>
                                                                {errors.prdue.message}
                                                            </span>
                                                        </div>
                                                    )}
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                    <div className={'table-responsive'}>
                                        <table className={'table mb-0'}>
                                            <thead>
                                                <tr>
                                                    <th>{`Designation Number`}</th>
                                                    <th>{`Designation Title`}</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {citations &&
                                                    citations.length > 0 &&
                                                    citations.map((c) => (
                                                        <tr key={c.citationId}>
                                                            <td>{c.designationNumber}</td>
                                                            <td>{c.designationTitle}</td>
                                                            <td className={'text-right'}>
                                                                <ButtonGroup>
                                                                    <Button
                                                                        type={'button'}
                                                                        color={'primary'}
                                                                        onClick={() => viewCitation(c)}
                                                                    >
                                                                        {`Edit`}
                                                                    </Button>
                                                                    <Button
                                                                        type={'button'}
                                                                        color={'danger'}
                                                                        onClick={() => {
                                                                            setRemoveCitation(c);
                                                                            toggleCitationRemoveModal();
                                                                        }}
                                                                    >
                                                                        {`Remove`}
                                                                    </Button>
                                                                </ButtonGroup>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                {citations && citations.length === 0 && (
                                                    <tr>
                                                        <td colSpan={3}>{`No Citations`}</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </Card>
                            </Col>
                            <Col xs={12} lg={6}>
                                <Card>
                                    <CardHeader className={'bg-white'}>
                                        <h4>
                                            <div className={'d-flex align-items-center'}>
                                                <span className={'mr-3'}>{`Program Director`}</span>
                                                {canMerge && (
                                                    <div className={'custom-control custom-checkbox mr-2'}>
                                                        <Controller
                                                            control={control}
                                                            name={'isProgramDirectorSectionComplete'}
                                                            render={({ field }) => (
                                                                <Input
                                                                    type={'checkbox'}
                                                                    className={'custom-control-input'}
                                                                    id={'pdSection'}
                                                                    checked={field.value || false}
                                                                    onChange={(e) => field.onChange(e.target.checked)}
                                                                />
                                                            )}
                                                        />
                                                        <Label
                                                            className={'custom-control-label font-weight-normal'}
                                                            for="pdSection"
                                                        >{`Accept`}</Label>
                                                    </div>
                                                )}
                                            </div>
                                        </h4>
                                    </CardHeader>
                                    <CardBody>
                                        <Row>
                                            <Col xs={12} lg={6}>
                                                <FormGroup>
                                                    <Label>
                                                        {`Email`}{' '}
                                                        {isLookingUpPD && (
                                                            <Spinner size="sm" color="primary" className="ms-1" />
                                                        )}
                                                    </Label>
                                                    <Controller
                                                        control={control}
                                                        name={'programDirectorEmail'}
                                                        render={({ field }) => (
                                                            <Input
                                                                type={'text'}
                                                                {...field}
                                                                value={field.value ?? ''}
                                                                onBlur={(e) => {
                                                                    field.onBlur();
                                                                    lookupProgramDirectorByEmail(e.target.value);
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                    {pdLookupMessage && (
                                                        <span className="text-success small">
                                                            {pdLookupMessage}
                                                        </span>
                                                    )}
                                                    {errors?.programDirectorEmail?.message && (
                                                        <div className={'mt-1'}>
                                                            <span className={'text-danger'}>
                                                                {errors.programDirectorEmail.message}
                                                            </span>
                                                        </div>
                                                    )}
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Salutations`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'programDirectorSalutations'}
                                                        render={({ field }) => <Input type={'text'} disabled={!isPDEmailEntered} {...field} value={field.value ?? ''} />}
                                                    />
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`First Name`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'programDirectorFirstName'}
                                                        render={({ field }) => <Input type={'text'} disabled={!isPDEmailEntered} {...field} value={field.value ?? ''} />}
                                                    />
                                                    {errors?.programDirectorFirstName?.message && (
                                                        <div className={'mt-1'}>
                                                            <span className={'text-danger'}>
                                                                {errors.programDirectorFirstName.message}
                                                            </span>
                                                        </div>
                                                    )}
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Phone`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'programDirectorPhone'}
                                                        render={({ field }) => <Input type={'text'} disabled={!isPDEmailEntered} {...field} value={field.value ?? ''} />}
                                                    />
                                                    {errors?.programDirectorPhone?.message && (
                                                        <div className={'mt-1'}>
                                                            <span className={'text-danger'}>
                                                                {errors.programDirectorPhone.message}
                                                            </span>
                                                        </div>
                                                    )}
                                                </FormGroup>
                                            </Col>
                                            <Col xs={12} lg={6}>
                                                <FormGroup>
                                                    <Label>{`Job Title`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'programDirectorTitle'}
                                                        render={({ field }) => <Input type={'text'} disabled={!isPDEmailEntered} {...field} value={field.value ?? ''} />}
                                                    />
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Credentials`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'programDirectorCredentials'}
                                                        render={({ field }) => <Input type={'text'} disabled={!isPDEmailEntered} {...field} value={field.value ?? ''} />}
                                                    />
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Last Name`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'programDirectorLastName'}
                                                        render={({ field }) => <Input type={'text'} disabled={!isPDEmailEntered} {...field} value={field.value ?? ''} />}
                                                    />
                                                    {errors?.programDirectorLastName?.message && (
                                                        <div className={'mt-1'}>
                                                            <span className={'text-danger'}>
                                                                {errors.programDirectorLastName.message}
                                                            </span>
                                                        </div>
                                                    )}
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Extension`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'programDirectorExtension'}
                                                        render={({ field }) => <Input type={'text'} disabled={!isPDEmailEntered} {...field} value={field.value ?? ''} />}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col xs={12} lg={6}>
                                <Card>
                                    <CardHeader className={'bg-white'}>
                                        <h4>
                                            <div className={'d-flex align-items-center'}>
                                                <span className={'mr-3'}>{`Dean`}</span>
                                                <div className={'custom-control custom-checkbox mr-2'}>
                                                    <Controller
                                                        control={control}
                                                        name={'deanNotAvailable'}
                                                        render={({ field }) => (
                                                            <Input
                                                                type={'checkbox'}
                                                                className={'custom-control-input'}
                                                                id={'dna'}
                                                                checked={field.value || false}
                                                                onChange={(e) => field.onChange(e.target.checked)}
                                                            />
                                                        )}
                                                    />
                                                    <Label
                                                        className={'custom-control-label font-weight-normal'}
                                                        for="dna"
                                                    >{`Dean Not Available`}</Label>
                                                </div>
                                                {canMerge && (
                                                    <div className={'custom-control custom-checkbox mr-2'}>
                                                        <Controller
                                                            control={control}
                                                            name={'isDeanSectionComplete'}
                                                            render={({ field }) => (
                                                                <Input
                                                                    type={'checkbox'}
                                                                    className={'custom-control-input'}
                                                                    id={'deanSection'}
                                                                    checked={field.value || false}
                                                                    onChange={(e) => field.onChange(e.target.checked)}
                                                                />
                                                            )}
                                                        />
                                                        <Label
                                                            className={'custom-control-label font-weight-normal'}
                                                            for="deanSection"
                                                        >{`Accept`}</Label>
                                                    </div>
                                                )}
                                            </div>
                                        </h4>
                                    </CardHeader>
                                    <CardBody>
                                        <Row>
                                            <Col xs={12} lg={6}>
                                                <FormGroup>
                                                    <Label>
                                                        {`Email`}{' '}
                                                        {isLookingUpDean && (
                                                            <Spinner size="sm" color="primary" className="ms-1" />
                                                        )}
                                                    </Label>
                                                    <Controller
                                                        control={control}
                                                        name={'deanEmail'}
                                                        render={({ field }) => (
                                                            <Input
                                                                type={'text'}
                                                                {...field}
                                                                value={field.value ?? ''}
                                                                onBlur={(e) => {
                                                                    field.onBlur();
                                                                    lookupDeanByEmail(e.target.value);
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                    {deanLookupMessage && (
                                                        <span className="text-success small">
                                                            {deanLookupMessage}
                                                        </span>
                                                    )}
                                                    {errors?.deanEmail?.message && (
                                                        <div className={'mt-1'}>
                                                            <span className={'text-danger'}>
                                                                {errors.deanEmail.message}
                                                            </span>
                                                        </div>
                                                    )}
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Salutations`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'deanSalutations'}
                                                        render={({ field }) => <Input type={'text'} disabled={!isDeanEmailEntered} {...field} value={field.value ?? ''} />}
                                                    />
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`First Name`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'deanFirstName'}
                                                        render={({ field }) => <Input type={'text'} disabled={!isDeanEmailEntered} {...field} value={field.value ?? ''} />}
                                                    />
                                                    {errors?.deanFirstName?.message && (
                                                        <div className={'mt-1'}>
                                                            <span className={'text-danger'}>
                                                                {errors.deanFirstName.message}
                                                            </span>
                                                        </div>
                                                    )}
                                                </FormGroup>
                                            </Col>
                                            <Col xs={12} lg={6}>
                                                <FormGroup>
                                                    <Label>{`Job Title`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'deanTitle'}
                                                        render={({ field }) => <Input type={'text'} disabled={!isDeanEmailEntered} {...field} value={field.value ?? ''} />}
                                                    />
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Credentials`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'deanCredentials'}
                                                        render={({ field }) => <Input type={'text'} disabled={!isDeanEmailEntered} {...field} value={field.value ?? ''} />}
                                                    />
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Last Name`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'deanLastName'}
                                                        render={({ field }) => <Input type={'text'} disabled={!isDeanEmailEntered} {...field} value={field.value ?? ''} />}
                                                    />
                                                    {errors?.deanLastName?.message && (
                                                        <div className={'mt-1'}>
                                                            <span className={'text-danger'}>
                                                                {errors.deanLastName.message}
                                                            </span>
                                                        </div>
                                                    )}
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col xs={12} lg={6}>
                                <Card>
                                    <CardHeader className={'bg-white'}>
                                        <h4>
                                            <div className={'d-flex align-items-center'}>
                                                <span className={'mr-3'}>{`President / CEO`}</span>
                                                {canMerge && (
                                                    <div className={'custom-control custom-checkbox mr-2'}>
                                                        <Controller
                                                            control={control}
                                                            name={'isCeoSectionComplete'}
                                                            render={({ field }) => (
                                                                <Input
                                                                    type={'checkbox'}
                                                                    className={'custom-control-input'}
                                                                    id={'ceoSection'}
                                                                    checked={field.value || false}
                                                                    onChange={(e) => field.onChange(e.target.checked)}
                                                                />
                                                            )}
                                                        />
                                                        <Label
                                                            className={'custom-control-label font-weight-normal'}
                                                            for="ceoSection"
                                                        >{`Accept`}</Label>
                                                    </div>
                                                )}
                                            </div>
                                        </h4>
                                    </CardHeader>
                                    <CardBody>
                                        <Row>
                                            <Col xs={12} lg={6}>
                                                <FormGroup>
                                                    <Label>
                                                        {`Email`}{' '}
                                                        {isLookingUpCeo && (
                                                            <Spinner size="sm" color="primary" className="ms-1" />
                                                        )}
                                                    </Label>
                                                    <Controller
                                                        control={control}
                                                        name={'ceoemail'}
                                                        render={({ field }) => (
                                                            <Input
                                                                type={'text'}
                                                                {...field}
                                                                value={field.value ?? ''}
                                                                onBlur={(e) => {
                                                                    field.onBlur();
                                                                    lookupCeoByEmail(e.target.value);
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                    {ceoLookupMessage && (
                                                        <span className="text-success small">
                                                            {ceoLookupMessage}
                                                        </span>
                                                    )}
                                                    {errors?.ceoemail?.message && (
                                                        <div className={'mt-1'}>
                                                            <span className={'text-danger'}>
                                                                {errors?.ceoemail?.message}
                                                            </span>
                                                        </div>
                                                    )}
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Salutations`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'ceosalutations'}
                                                        render={({ field }) => <Input type={'text'} disabled={!isCeoEmailEntered} {...field} value={field.value ?? ''} />}
                                                    />
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`First Name`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'ceofirstName'}
                                                        render={({ field }) => <Input type={'text'} disabled={!isCeoEmailEntered} {...field} value={field.value ?? ''} />}
                                                    />
                                                    {errors?.ceofirstName?.message && (
                                                        <div className={'mt-1'}>
                                                            <span className={'text-danger'}>
                                                                {errors.ceofirstName.message}
                                                            </span>
                                                        </div>
                                                    )}
                                                </FormGroup>
                                            </Col>
                                            <Col xs={12} lg={6}>
                                                <FormGroup>
                                                    <Label>{`Job Title`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'ceotitle'}
                                                        render={({ field }) => <Input type={'text'} disabled={!isCeoEmailEntered} {...field} value={field.value ?? ''} />}
                                                    />
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Credentials`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'ceocredentials'}
                                                        render={({ field }) => <Input type={'text'} disabled={!isCeoEmailEntered} {...field} value={field.value ?? ''} />}
                                                    />
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>{`Last Name`}</Label>
                                                    <Controller
                                                        control={control}
                                                        name={'ceolastName'}
                                                        render={({ field }) => <Input type={'text'} disabled={!isCeoEmailEntered} {...field} value={field.value ?? ''} />}
                                                    />
                                                    {errors?.ceolastName?.message && (
                                                        <div className={'mt-1'}>
                                                            <span className={'text-danger'}>
                                                                {errors?.ceolastName?.message}
                                                            </span>
                                                        </div>
                                                    )}
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>

                        <Row>
                            <Col>
                                <Card>
                                    <CardFooter>
                                        {/* Error Summary Section */}
                                        {formState.isSubmitted && (!formState.isValid || (isMergeCommand && !ableToMerge(recommendation))) && (
                                            <Alert color={'danger'} className="mb-3">
                                                <h6 className="alert-heading mb-2">
                                                    <i className="mdi mdi-alert-circle-outline mr-2"></i>
                                                    {isMergeCommand ? 'Cannot merge recommendation' : 'Cannot save recommendation'}
                                                </h6>
                                                
                                                {/* Section Completion Errors for Merge */}
                                                {isMergeCommand && !ableToMerge(recommendation) && (
                                                    <div className="mb-3">
                                                        <strong>Incomplete sections:</strong>
                                                        <ul className="mb-0 mt-2">
                                                            {!recommendation.isInstitutionSectionComplete && (
                                                                <li>Institution section is incomplete</li>
                                                            )}
                                                            {!recommendation.isProgramSectionComplete && (
                                                                <li>Program section is incomplete</li>
                                                            )}
                                                            {!recommendation.isProgramDirectorSectionComplete && (
                                                                <li>Program Director section is incomplete</li>
                                                            )}
                                                            {!recommendation.isDeanSectionComplete && (
                                                                <li>Dean section is incomplete</li>
                                                            )}
                                                            {!recommendation.isCeoSectionComplete && (
                                                                <li>CEO section is incomplete</li>
                                                            )}
                                                            {!recommendation.isCitationSectionComplete && (
                                                                <li>Citation section is incomplete</li>
                                                            )}
                                                            {!recommendation.isRecommendationSectionComplete && (
                                                                <li>Recommendation section is incomplete</li>
                                                            )}
                                                        </ul>
                                                    </div>
                                                )}
                                                
                                                {/* Field Validation Errors */}
                                                {!formState.isValid && Object.keys(errors).length > 0 && (
                                                    <div>
                                                        <strong>Field errors:</strong>
                                                        <ul className="mb-0 mt-2">
                                                            {Object.keys(errors).map((fieldName) => {
                                                                const fieldError = errors[fieldName];
                                                                if (fieldError?.message) {
                                                                    // Map field names to user-friendly labels
                                                                    const fieldLabels: { [key: string]: string } = {
                                                                        programAddress: 'Program Address',
                                                                        programCity: 'Program City',
                                                                        programState: 'Program State',
                                                                        programZip: 'Program Zip',
                                                                        programCountry: 'Program Country',
                                                                        programDateFirstAccredited: 'Date First Accredited',
                                                                        degreeTypeId: 'Degree Type',
                                                                        effectiveDate: 'Effective Date',
                                                                        nextFullEval: 'Next Full Evaluation',
                                                                        prdue: 'Program Report Due',
                                                                        programDirectorFirstName: 'Program Director First Name',
                                                                        programDirectorLastName: 'Program Director Last Name',
                                                                        programDirectorEmail: 'Program Director Email',
                                                                        programDirectorPhone: 'Program Director Phone',
                                                                        deanFirstName: 'Dean First Name',
                                                                        deanLastName: 'Dean Last Name',
                                                                        deanEmail: 'Dean Email',
                                                                        ceofirstName: 'CEO First Name',
                                                                        ceolastName: 'CEO Last Name',
                                                                        ceoemail: 'CEO Email',
                                                                        meetingDateId: 'Meeting Date',
                                                                        statusId: 'Recommendation Status',
                                                                        institutionalAccreditorId: 'Institutional Accreditor'
                                                                    };
                                                                    
                                                                    const friendlyName = fieldLabels[fieldName] || fieldName;
                                                                    return (
                                                                        <li key={fieldName}>
                                                                            <strong>{friendlyName}:</strong> {fieldError.message}
                                                                        </li>
                                                                    );
                                                                }
                                                                return null;
                                                            })}
                                                        </ul>
                                                    </div>
                                                )}
                                            </Alert>
                                        )}
                                        
                                        <div className={'d-flex justify-content-between align-items-center'}>
                                            <div>
                                                {canMerge && (
                                                    <>
                                                        {!hasBeenMerged() && (
                                                            <>
                                                                <Button
                                                                    color={'dark'}
                                                                    type={'button'}
                                                                    className={'mr-3'}
                                                                    onClick={() => acceptAll()}
                                                                >
                                                                    {`Accept All`}
                                                                </Button>
                                                                <Button
                                                                    color={'primary'}
                                                                    type={'submit'}
                                                                    className={'mr-3'}
                                                                    onClick={() => setIsMergeCommand(true)}
                                                                    disabled={!ableToMerge(recommendation)}
                                                                >
                                                                    {`Merge Recommendation`}
                                                                </Button>
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                                {!hasBeenMerged() && (
                                                    <Button
                                                        color={'info'}
                                                        type={'submit'}
                                                        onClick={() => setIsMergeCommand(false)}
                                                    >
                                                        {`Save Recommendation`}
                                                    </Button>
                                                )}
                                            </div>
                                            {!hasBeenMerged() && (
                                                <Button
                                                    color={`danger`}
                                                    type={'button'}
                                                    onClick={() => toggleRemoveModal()}
                                                >
                                                    {`${isNew ? 'Cancel' : 'Delete'} Recommendation`}
                                                </Button>
                                            )}
                                        </div>
                                    </CardFooter>
                                </Card>
                            </Col>
                        </Row>
                    </Form>
                )}

                {selectedCitation && recId && recommendation.programProfessionId && (
                    <Modal isOpen={showCitationModal} toggle={toggleCitationModal} size={'lg'}>
                        <CitationForm
                            citation={selectedCitation}
                            professionId={recommendation.programProfessionId}
                            onCancel={toggleCitationModal}
                            onSave={() => {
                                setReloadCitations(true);
                                toggleCitationModal();
                            }}
                        />
                    </Modal>
                )}

                {removeCitation && (
                    <Modal isOpen={showCitationRemoveModal} toggle={toggleCitationRemoveModal}>
                        <ModalHeader>
                            <h4>{`Remove Citation`}</h4>
                        </ModalHeader>
                        <ModalBody>
                            <p>
                                {`To confirm you would like to remove the citation for Designation Number ${removeCitation.designationNumber}, please click the 'Confirm' button below.`}
                            </p>
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                type={'button'}
                                color={'primary'}
                                onClick={() => deleteCitation(removeCitation.citationId)}
                            >
                                {`Confirm`}
                            </Button>
                            <Button
                                type={'button'}
                                color={'secondary'}
                                className={'ml-3'}
                                onClick={() => toggleCitationRemoveModal()}
                            >
                                {`Cancel`}
                            </Button>
                        </ModalFooter>
                    </Modal>
                )}

                <Modal isOpen={showRemoveModal} toggle={toggleRemoveModal}>
                    <ModalHeader>
                        <h4>{`Delete Recommendation`}</h4>
                    </ModalHeader>
                    <ModalBody>
                        <p>
                            {`To confirm you would like to delete this recommendation, please click 'Confirm' below.`}
                        </p>
                    </ModalBody>
                    <ModalFooter>
                        <Button type={'button'} color={'primary'} onClick={() => deleteRecommendation(recId)}>
                            {`Confirm`}
                        </Button>
                        <Button
                            type={'button'}
                            color={'secondary'}
                            className={'ml-3'}
                            onClick={() => toggleRemoveModal()}
                        >
                            {`Cancel`}
                        </Button>
                    </ModalFooter>
                </Modal>

                <Modal isOpen={showChangeInst} toggle={toggleShowChangeInst} centered={true}>
                    <ModalHeader>
                        <h4>{`Change Institution`}</h4>
                    </ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label>{`Institution ID`}</Label>
                            <Input
                                type={'text'}
                                value={changeInstId || ''}
                                disabled={changeNewInst}
                                onChange={(e) => setChangeInstId(e.target.value)}
                            />
                        </FormGroup>
                        {+(recommendation.caahepinstitutionId || 0) > 0 && (
                            <div className={'custom-control custom-checkbox mr-2'}>
                                <Input
                                    type={'checkbox'}
                                    className={'custom-control-input'}
                                    id={'instSetNew'}
                                    checked={changeNewInst}
                                    onChange={(e) => {
                                        setChangeNewInst(e.target.checked);
                                        if (e.target.checked) {
                                            setChangeInstId(undefined);
                                        }
                                    }}
                                />
                                <Label
                                    className={'custom-control-label font-weight-normal'}
                                    for="instSetNew"
                                >{`Set to New Institution`}</Label>
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button type={'button'} color={'primary'} onClick={() => updateInstitution()}>
                            {`Submit`}
                        </Button>
                        <Button
                            type={'button'}
                            color={'secondary'}
                            className={'ml-3'}
                            onClick={() => {
                                toggleShowChangeInst();
                                setChangeInstId(undefined);
                                setChangeNewInst(false);
                            }}
                        >
                            {`Cancel`}
                        </Button>
                    </ModalFooter>
                </Modal>

                {editSatellite && (
                    <Modal isOpen={showSatellite} toggle={toggleShowSatellite} centered>
                        <RecommendationSatelliteForm
                            satellite={editSatellite}
                            onCancel={toggleShowSatellite}
                            onSave={() => {
                                setReloadSatellites(true);
                                toggleShowSatellite();
                            }}
                        />
                    </Modal>
                )}

                <Modal isOpen={showExtensionModal} toggle={toggleShowExtensionModal} centered>
                    <ModalHeader toggle={toggleShowExtensionModal}>
                        {editExtension?.extensionTypeId === 2 ? 'Add Concentration' : 'Add Add-On Track'}
                    </ModalHeader>
                    <ModalBody>
                        {editExtension?.extensionTypeId === 2 && profession?.professionsConcentrations && (
                            <FormGroup>
                                <Label>Select Concentration</Label>
                                <Input
                                    type="select"
                                    value={editExtension?.concentrationId || ''}
                                    onChange={(e) => setEditExtension({
                                        ...editExtension,
                                        concentrationId: parseInt(e.target.value),
                                    })}
                                >
                                    <option value="">Select...</option>
                                    {profession.professionsConcentrations
                                        .filter(c => !c.isDeleted && !extensions.some(e =>
                                            e.concentrationId === c.concentrationId &&
                                            !e.isDeleted &&
                                            e.activeStatus !== ExtensionStatus.Removed))
                                        .map(c => (
                                            <option key={c.concentrationId} value={c.concentrationId}>
                                                {c.title}
                                            </option>
                                        ))}
                                </Input>
                            </FormGroup>
                        )}
                        
                        {editExtension?.extensionTypeId === 1 && profession?.professionAddOnTracks && (
                            <FormGroup>
                                <Label>Select Add-On Track</Label>
                                <Input
                                    type="select"
                                    value={editExtension?.addOnTrackId || ''}
                                    onChange={(e) => setEditExtension({
                                        ...editExtension,
                                        addOnTrackId: parseInt(e.target.value),
                                    })}
                                >
                                    <option value="">Select...</option>
                                    {profession.professionAddOnTracks
                                        .filter(a => !extensions.some(e =>
                                            e.addOnTrackId === a.addOnTrackId &&
                                            !e.isDeleted &&
                                            e.activeStatus !== ExtensionStatus.Removed))
                                        .map(a => (
                                            <option key={a.addOnTrackId} value={a.addOnTrackId}>
                                                {a.title}
                                            </option>
                                        ))}
                                </Input>
                            </FormGroup>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button 
                            color="primary" 
                            onClick={() => {
                                if (editExtension && 
                                    ((editExtension.extensionTypeId === 2 && editExtension.concentrationId) ||
                                     (editExtension.extensionTypeId === 1 && editExtension.addOnTrackId))) {
                                    const itemType = editExtension.extensionTypeId === 2 ? 'concentration' : 'add-on track';
                                    const toastId = toast.info(`Adding ${itemType}...`);
                                    recommendationService
                                        .saveExtension(editExtension)
                                        .then(() => {
                                            toast.update(toastId, {
                                                render: `${itemType.charAt(0).toUpperCase() + itemType.slice(1)} added successfully`,
                                                type: 'success',
                                            });
                                            setReloadExtensions(true);
                                            toggleShowExtensionModal();
                                            setEditExtension(undefined);
                                        })
                                        .catch(() => {
                                            toast.update(toastId, {
                                                render: `Error adding ${itemType}`,
                                                type: 'error',
                                            });
                                        });
                                }
                            }}
                            disabled={!editExtension || 
                                (editExtension.extensionTypeId === 2 && !editExtension.concentrationId) ||
                                (editExtension.extensionTypeId === 1 && !editExtension.addOnTrackId)}
                        >
                            Add
                        </Button>
                        <Button color="secondary" onClick={() => {
                            toggleShowExtensionModal();
                            setEditExtension(undefined);
                        }}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </Modal>
            </>
        );
    }
};

export default Recommendation;
